import { StylomeLargeTransparencyReport } from "./StylomeLargeTransparencyReport";
import { BenchmarkMetric } from "./BenchmarkMetric";
import React, { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import {
  examples,
  scoreText,
  labelCopy,
  normalizeText,
  type ScoreResult,
  PUBLIC_RESPONSE_VERSION,
} from "./api";
import {
  DOCUMENT_FILE_ACCEPT,
  importDocument,
  type ImportedDocument,
} from "./document-import";
import { canonicalForRoute, SOCIAL_IMAGE, seoForRoute } from "./seo";
import { StylomeOneTransparencyReport } from "./StylomeOneTransparencyReport";
import { SupportedSites } from "./SupportedSites";
import { turnstileToken } from "./turnstile";
import "./styles.css";
import { Slopwatch } from "./Slopwatch";
import { ApiCredits } from "./ApiCredits";
import {
  STORE_CLICK_EVENT,
  trackEvent,
  trackInstallOnce,
  trackPage,
} from "./analytics";

const nav = [
  ["/", "Scan"],
  ["/transparency", "Transparency"],
  ["/pricing", "Pricing"],
  ["/extension", "Extension"],
  ["/supported-sites", "Supported sites"],
  ["/developers", "Developer API"],
];
const googleAuthUrl = (returnTo = "/account") =>
  `/api/v1/auth/google/start?return_to=${encodeURIComponent(returnTo)}`;
function path() {
  if (typeof window === "undefined") return "/";
  return window.location.pathname.replace(/\/$/, "") || "/";
}
function go(to: string) {
  history.pushState({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}

function Link({
  to,
  children,
  className = "",
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a
      href={to}
      className={className}
      onClick={(e) => {
        if (!e.metaKey && !e.ctrlKey) {
          e.preventDefault();
          go(to);
        }
      }}
    >
      {children}
    </a>
  );
}

function Header({ route }: { route: string }) {
  const [open, setOpen] = useState(false);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  useEffect(() => {
    setOpen(false);
  }, [route]);
  useEffect(() => {
    let active = true;
    fetch("/api/v1/me")
      .then((response) => response.json())
      .then((body) => {
        if (active) setAuthenticated(Boolean(body.authenticated));
      })
      .catch(() => {
        if (active) setAuthenticated(false);
      });
    return () => {
      active = false;
    };
  }, [route]);

  async function logout() {
    await fetch("/api/v1/auth/logout", { method: "POST" });
    location.href = "/";
  }

  const authActions = authenticated ? (
    <>
      <Link to="/account" className="text-button">
        My account
      </Link>
      <button className="text-button" onClick={logout}>
        Sign out
      </button>
    </>
  ) : authenticated === false ? (
    <Link className="text-button" to="/login">
      Sign in
    </Link>
  ) : null;
  return (
    <header className="site-header">
      <Link to="/" className="brand" aria-label="Stylome home">
        <img
          className="brand-mark"
          src="/logo-mark.svg"
          alt=""
          width="32"
          height="32"
        />
        <span>Stylome</span>
      </Link>
      <button
        className="menu"
        type="button"
        aria-label="Menu"
        aria-controls="primary-navigation"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24">
          {open ? (
            <path d="M5 5l14 14M19 5 5 19" />
          ) : (
            <path d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>
      <nav
        id="primary-navigation"
        className={open ? "open" : ""}
        aria-label="Primary navigation"
      >
        {nav.map(([to, label]) => (
          <Link key={to} to={to} className={route === to ? "active" : ""}>
            {label}
          </Link>
        ))}
        <div className="nav-auth-actions">{authActions}</div>
      </nav>
      <div className="auth-actions">{authActions}</div>
    </header>
  );
}

function Footer() {
  return (
    <footer>
      <div>
        <span className="footer-brand">
          <img src="/logo-signature.svg" alt="" width="32" height="32" />
          <strong>Stylome</strong>
        </span>
        <p>AI-text detection built on published, reproducible benchmarks.</p>
      </div>
      <div className="footer-links">
        <Link to="/methodology">Methodology</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <a href="mailto:support@stylome.io">Support</a>
        <a href="https://discord.gg/YTq23pF6AM">Discord</a>
      </div>
    </footer>
  );
}

function Connectivity() {
  const [offline, setOffline] = useState(
    typeof window === "undefined" ? false : !navigator.onLine,
  );
  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    addEventListener("online", update);
    addEventListener("offline", update);
    return () => {
      removeEventListener("online", update);
      removeEventListener("offline", update);
    };
  }, []);
  return offline ? (
    <div className="connectivity" role="status">
      You are offline. Existing pages remain readable; scanning requires a
      connection.
    </div>
  ) : null;
}

function Layout({
  route,
  children,
}: {
  route: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Connectivity />
      <Header route={route} />
      <main id="main">{children}</main>
      <noscript>
        <p className="connectivity">
          JavaScript is required for scanning, account controls, and the writing
          game. This page's reference content remains readable.
        </p>
      </noscript>
      <Footer />
    </>
  );
}

function Result({
  result,
  onReceipt,
}: {
  result: ScoreResult;
  onReceipt?: () => void;
}) {
  const copy = labelCopy[result.label];
  const tone =
    result.label === "human_written"
      ? "human_typical"
      : result.label === "ai_edited"
        ? "mixed_signals"
        : "ai_typical";
  return (
    <section className={`result verdict-${tone}`} aria-live="polite">
      {result.mock && (
        <div className="mock-ribbon">
          Demonstration result · inference model not connected
        </div>
      )}
      <div className="result-grid">
        <div>
          <h2>{copy.name}</h2>
          <p>{copy.note}</p>
        </div>
        <div className={`confidence-badge confidence-${result.confidence}`}>
          <strong>
            <svg
              className="confidence-signal"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                className={
                  result.confidence === "high"
                    ? "signal-active"
                    : "signal-inactive"
                }
                d="M3 8.5Q12 .5 21 8.5"
              />
              <path
                className={
                  result.confidence !== "low"
                    ? "signal-active"
                    : "signal-inactive"
                }
                d="M6 12Q12 6.5 18 12"
              />
              <path className="signal-active" d="M9 15.5Q12 12.5 15 15.5" />
              <circle className="signal-dot" cx="12" cy="19" r="1.3" />
            </svg>
            {result.confidence[0].toUpperCase() + result.confidence.slice(1)}{" "}
            confidence
          </strong>
        </div>
      </div>
      <dl className="result-meta">
        <div>
          <dt>Model</dt>
          <dd>{result.model_version}</dd>
        </div>
        <div>
          <dt>Receipt time</dt>
          <dd>{new Date(result.created_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt>Content hash</dt>
          <dd>{result.content_hash.slice(0, 12)}</dd>
        </div>
      </dl>
      <div className="result-actions">
        {onReceipt && (
          <button className="button primary" onClick={onReceipt}>
            Share
          </button>
        )}
        <Link to="/transparency" className="button secondary">
          How thresholds work
        </Link>
      </div>
    </section>
  );
}

function Scanner() {
  const [text, setText] = useState("");
  const [importedDocument, setImportedDocument] =
    useState<ImportedDocument | null>(null);
  const [importedSectionId, setImportedSectionId] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [result, setResult] = useState<ScoreResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [receiptBusy, setReceiptBusy] = useState(false);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const workspace = useRef<HTMLDivElement>(null);
  const importSequence = useRef(0);
  const count = Array.from(normalizeText(text)).length;
  const valid = count >= 200 && count <= 10_000;
  useEffect(() => {
    if (result) workspace.current?.scrollIntoView();
  }, [result]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setNotice("");
    if (!valid)
      return setError(
        count < 200
          ? `Add ${200 - count} more characters for a meaningful result.`
          : "Text must be 10,000 characters or fewer.",
      );
    setBusy(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 90_000);
    try {
      if (!turnstileContainer.current)
        throw new Error("Human verification could not initialize.");
      const token = await turnstileToken(turnstileContainer.current);
      setResult(await scoreText(text, token, controller.signal));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "The scan could not be completed.";
      if (message.includes("Human verification")) {
        setError(message);
      } else {
        setResult(null);
        setNotice(
          err instanceof DOMException && err.name === "AbortError"
            ? "The scanner took too long to start. Your text was not scored; please retry."
            : `The scanner is unavailable. Your text was not scored: ${message}`,
        );
      }
    } finally {
      clearTimeout(timeout);
      setBusy(false);
    }
  }
  async function selectDocument(file: File | undefined) {
    if (!file) return;
    const sequence = ++importSequence.current;
    setImportBusy(true);
    setError("");
    setNotice("");
    try {
      const imported = await importDocument(file);
      if (sequence !== importSequence.current) return;
      setText(imported.text);
      setImportedDocument(imported);
      setImportedSectionId(imported.initialSectionId);
      setResult(null);
    } catch (err) {
      if (sequence !== importSequence.current) return;
      setError(
        err instanceof Error
          ? err.message
          : "Stylome could not read this document.",
      );
    } finally {
      if (sequence === importSequence.current) setImportBusy(false);
    }
  }
  async function receipt() {
    if (!result?.receipt) return;
    setReceiptBusy(true);
    setError("");
    try {
      const response = await fetch(
        `/api/v1/receipts/${result.receipt.id}/share`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ share_token: result.receipt.share_token }),
        },
      );
      const body = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          body?.error?.message || "Receipt could not be created.",
        );
      location.href = body.url;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Receipt could not be created.",
      );
      setReceiptBusy(false);
    }
  }
  return (
    <div className={`scanner-wrap ${result ? "has-result" : ""}`}>
      <div className="scanner-intro">
        <h1>Detect AI text</h1>
        <p>
          Check text for signs of AI generation. Paste a passage or import a
          DOCX or EPUB file. Free, with no sign-in required.
        </p>
      </div>
      <div className="scan-workspace" ref={workspace}>
        <form className="scanner" onSubmit={submit}>
          <div className="scanner-input-heading">
            <label htmlFor="scan-text">Text to inspect</label>
            <label className={`document-picker ${importBusy ? "busy" : ""}`}>
              <input
                type="file"
                accept={DOCUMENT_FILE_ACCEPT}
                disabled={importBusy || busy}
                aria-label="Upload a DOCX or EPUB file"
                onChange={(event) => {
                  void selectDocument(event.currentTarget.files?.[0]);
                  event.currentTarget.value = "";
                }}
              />
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
              </svg>
              <span>{importBusy ? "Reading file…" : "Upload file"}</span>
            </label>
          </div>
          <textarea
            id="scan-text"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setResult(null);
            }}
            placeholder="Paste an article, email, essay, or post…"
            aria-describedby="scan-help"
          />
          {importedDocument && (
            <div className="document-import-status" role="status">
              <div>
                <strong>{importedDocument.fileName}</strong>
                <span>
                  {importedDocument.warnings.length > 0
                    ? "Imported with parser warnings. Review the text before scanning."
                    : importedDocument.description}
                  {importedDocument.sections.length > 1 &&
                    ` ${importedDocument.sections.length.toLocaleString()} sections; ${importedDocument.totalCharacters.toLocaleString()} total characters.`}
                </span>
              </div>
              <button
                type="button"
                className="text-action"
                onClick={() => {
                  importSequence.current += 1;
                  setImportedDocument(null);
                  setImportedSectionId("");
                  setText("");
                  setResult(null);
                  setError("");
                  setNotice("");
                  setImportBusy(false);
                }}
              >
                Remove
              </button>
            </div>
          )}
          {importedDocument && importedDocument.sections.length > 1 && (
            <label className="document-section-picker">
              <span>Book section</span>
              <select
                value={importedSectionId}
                onChange={(event) => {
                  const section = importedDocument.sections.find(
                    (candidate) => candidate.id === event.target.value,
                  );
                  if (!section) return;
                  setImportedSectionId(section.id);
                  setText(section.text);
                  setResult(null);
                  setError("");
                  setNotice("");
                }}
              >
                {importedDocument.sections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.label} · {section.text.length.toLocaleString()}{" "}
                    chars
                  </option>
                ))}
              </select>
            </label>
          )}
          <div
            className={`character-rule ${count > 10_000 ? "over-limit" : ""}`}
            aria-hidden="true"
          >
            <span
              className="character-rule-fill"
              style={{ width: `${Math.min(count / 10_000, 1) * 100}%` }}
            />
            <span className="character-rule-minimum" />
          </div>
          <div className="input-meta" id="scan-help">
            <span>200 minimum · Files read locally</span>
            <span
              className={count > 10_000 ? "bad" : count >= 200 ? "good" : ""}
            >
              {count.toLocaleString()} / 10,000 characters
            </span>
          </div>
          <div className="scanner-actions">
            <button
              type="button"
              className="text-action"
              onClick={() => {
                setText(examples.human);
                setImportedDocument(null);
                setImportedSectionId("");
                setResult(null);
              }}
            >
              Use example
            </button>
            <button
              type="button"
              className="text-action"
              disabled={!text}
              onClick={() => {
                setText("");
                setImportedDocument(null);
                setImportedSectionId("");
                setResult(null);
                setError("");
                setNotice("");
              }}
            >
              Clear
            </button>
            <button className="button primary" disabled={busy || importBusy}>
              {busy
                ? "Inspecting…"
                : importBusy
                  ? "Reading file…"
                  : "Inspect text"}
            </button>
          </div>
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="alert notice" role="status">
              {notice}
            </p>
          )}
          <div
            ref={turnstileContainer}
            className="turnstile-mount"
            aria-live="polite"
          />
        </form>
        {result && <Result result={result} onReceipt={receipt} />}
      </div>
      {receiptBusy && (
        <p className="alert notice" role="status">
          Publishing receipt…
        </p>
      )}
    </div>
  );
}

function Home() {
  return (
    <>
      <Scanner />
      <section className="evidence-band">
        <h2>99% of AI-generated text detected.</h2>
        <p>
          With just 2 false positives across 10,036 human benchmark samples.{" "}
          <Link to="/transparency">Explore the benchmarks</Link>.
        </p>
      </section>
      <section className="evidence-band extension-band">
        <h2>See it on every page.</h2>
        <div>
          <p>
            The Stylome extension marks AI-written posts and comments as you
            browse Reddit and 400+ other sites.{" "}
            <Link to="/extension">How the extension works</Link>.
          </p>
          <InstallButton placement="home_band" />
        </div>
      </section>
      <Slopwatch />
    </>
  );
}

const transparencyModels = [
  {
    id: "stylome-1.2-large",
    name: "Stylome 1.2 Large",
    availability: "Published benchmark",
    purpose: "Our newest and best model.",
    reportStatus: "6 benchmark reports available",
  },
  {
    id: "stylome-1.0",
    name: "Stylome 1.0",
    availability: "Retired",
    purpose: "Benchmark record for the previous standard model.",
    reportStatus: "5 benchmark reports available",
  },
  {
    id: "stylome-fast-1.0",
    name: "Stylome 1.0 Fast",
    availability: "Retired",
    purpose: "Benchmark record for the retired fast model.",
    reportStatus: "6 benchmark reports available",
  },
] as const;

type TransparencyModelId = (typeof transparencyModels)[number]["id"];

const editLensResults = [
  {
    split: "Standard test",
    description: "Reviews, news, educational web text, and creative writing",
    passages: "6,115",
    accuracy: "88.4%",
    macroF1: "88.1%",
    humanRecall: "89.2%",
    editedRecall: "76.3%",
    generatedRecall: "99.6%",
    humanCleared: "99.90%",
    editedDetected: "63.56%",
    generatedDetected: "99.51%",
    falsePositives: "2 of 2,038",
  },
  {
    split: "Enron email",
    description: "A separate business-email domain",
    passages: "6,147",
    accuracy: "89.8%",
    macroF1: "89.8%",
    humanRecall: "97.3%",
    editedRecall: "75.8%",
    generatedRecall: "99.2%",
    humanCleared: "100%",
    editedDetected: "54.85%",
    generatedDetected: "99.13%",
    falsePositives: "0 of 1,992",
  },
  {
    split: "Llama",
    description: "AI text produced only with Llama 3.3",
    passages: "5,957",
    accuracy: "91.3%",
    macroF1: "91.1%",
    humanRecall: "89.2%",
    editedRecall: "84.3%",
    generatedRecall: "99.8%",
    humanCleared: "99.90%",
    editedDetected: "60.39%",
    generatedDetected: "99.71%",
    falsePositives: "2 of 2,038",
  },
];

const resultMeanings = [
  ["Human written", "The classifier found no material AI contribution."],
  [
    "AI edited",
    "The classifier found retained human authorship with an AI contribution. This does not estimate the percentage written by AI.",
  ],
  [
    "AI generated",
    "The classifier found fully machine-derived text, including machine-generated text rewritten by another model.",
  ],
];

const raidAssistantResults = [
  ["ChatGPT · GPT-3.5 Turbo", "96.48%", "99.76%"],
  ["Llama 2 70B Chat", "93.71%", "99.43%"],
  ["GPT-4", "83.96%", "98.97%"],
  ["Mistral 7B Instruct", "74.80%", "96.35%"],
  ["MPT-30B Chat", "59.44%", "85.24%"],
  ["Cohere Chat", "34.46%", "76.11%"],
];

const raidCompletionResults = [
  ["GPT-3 · text-davinci-002", "5.71%", "51.69%"],
  ["GPT-2 XL", "3.28%", "47.53%"],
  ["Cohere Generate", "4.62%", "31.86%"],
  ["Mistral 7B base", "2.46%", "33.09%"],
  ["MPT-30B base", "0.74%", "25.31%"],
];

const raidAttackResults = [
  ["Inserted paragraphs", "37.60%", "66.81%"],
  ["Number changes", "37.15%", "64.27%"],
  ["Alternative spelling", "33.28%", "60.86%"],
  ["Misspellings", "24.96%", "53.63%"],
  ["Homoglyphs", "2.76%", "53.57%"],
  ["Paraphrasing", "7.05%", "52.59%"],
  ["Synonym replacement", "6.93%", "52.27%"],
  ["Article deletion", "15.09%", "45.28%"],
  ["Alternating case", "6.21%", "36.67%"],
  ["Added whitespace", "3.46%", "31.25%"],
  ["Zero-width spaces", "0.003%", "18.37%"],
];

function ResultRows({ rows }: { rows: string[][] }) {
  return rows.map(([name, live, comparison]) => (
    <tr key={name}>
      <th scope="row">{name}</th>
      <td>{live}</td>
      <td>{comparison}</td>
    </tr>
  ));
}

function Transparency() {
  const [selectedModel, setSelectedModel] =
    useState<TransparencyModelId>("stylome-1.2-large");

  return (
    <Page title="How well does Stylome work?">
      <p className="lede">
        See exactly how each Stylome model performs across public benchmarks,
        writing types, and source domains. Every result is attributed to a model
        version, fixed threshold, test population, and overlap audit.
      </p>
      <section className="content-section">
        <div className="section-heading">
          <div>
            <h2>Choose a model</h2>
          </div>
        </div>
        <div
          className="model-report-list"
          role="group"
          aria-label="Transparency report model"
        >
          {transparencyModels.map((model) => (
            <button
              type="button"
              aria-pressed={selectedModel === model.id}
              className="model-report-card"
              key={model.id}
              onClick={() => setSelectedModel(model.id)}
            >
              <div>
                <span className="badge badge-good">{model.availability}</span>
                <h3>{model.name}</h3>
                <p>{model.purpose}</p>
              </div>
              <div className="model-report-status">
                <span>Published results</span>
                <strong>{model.reportStatus}</strong>
              </div>
            </button>
          ))}
        </div>
      </section>
      <div role="region" aria-label="Selected model transparency report">
        {selectedModel === "stylome-1.2-large" ? (
          <StylomeLargeTransparencyReport />
        ) : selectedModel === "stylome-1.0" ? (
          <StylomeOneTransparencyReport />
        ) : (
          <>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · RAID
                    </span>
                    <strong>RAID</strong>
                    <span>
                      A large independent test spanning eight kinds of writing,
                      eleven generators, and deliberate attempts to evade
                      detection.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="RAID highlights"
                  >
                    <BenchmarkMetric
                      label="Live threshold · human cleared"
                      value="99.90%"
                      context="13,342 of 13,356"
                    />
                    <BenchmarkMetric
                      label="RAID comparison · assistants detected"
                      value="93.01%"
                      context="Clean assistant output; count not reported"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    Two operating points answer different questions. Stylome 1.0
                    Fast’s recorded production threshold shows that model's
                    deployed behavior. RAID’s standard comparison allows a 5%
                    false-positive rate in each domain, as its published
                    evaluation protocol specifies.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      <a
                        href="https://github.com/liamdugan/raid"
                        target="_blank"
                        rel="noreferrer"
                      >
                        RAID
                      </a>{" "}
                      covers abstracts, books, news, poetry, recipes, Reddit,
                      reviews, and Wikipedia. Stylome scored the complete
                      labeled training release: 467,985 clean passages and all
                      eleven published transformations, or 5,615,820 rows in
                      total.
                    </p>
                    <p>
                      None of the passages had full-text or prefix overlap with
                      Stylome’s final training corpus. The generators date to
                      2023 and were not represented in Stylome’s training data.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>99.90% human writing cleared</strong> at the live
                      threshold: 13,342 of 13,356 eligible clean passages.
                    </p>
                    <p>
                      <strong>74.59% assistant output detected</strong> at the
                      same conservative threshold; this rises to{" "}
                      <strong>93.01%</strong>
                      under RAID’s matched 5%-false-positive comparison.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="RAID assistant results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>Clean output from 2023 assistant models</caption>
                      <thead>
                        <tr>
                          <th>Generator</th>
                          <th>Live threshold</th>
                          <th>RAID comparison</th>
                        </tr>
                      </thead>
                      <tbody>
                        <ResultRows rows={raidAssistantResults} />
                      </tbody>
                    </table>
                  </div>
                  <div className="benchmark-heading">
                    <h3>Legacy instruction and base-model completions</h3>
                    <p>
                      RAID also contains older completion systems. These models
                      sample much closer to the human writing they learned from
                      and do not represent the polished assistant products most
                      people use today. They remain in the complete results
                      rather than being folded into the assistant headline.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="RAID completion model results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Clean legacy instruction and base-model output
                      </caption>
                      <thead>
                        <tr>
                          <th>Generator</th>
                          <th>Live threshold</th>
                          <th>RAID comparison</th>
                        </tr>
                      </thead>
                      <tbody>
                        <ResultRows rows={raidCompletionResults} />
                      </tbody>
                    </table>
                  </div>
                  <div className="benchmark-heading">
                    <h3>All eleven RAID transformations</h3>
                    <p>
                      Each clean passage was also mechanically altered. These
                      are reported separately because they test deliberate
                      evasion, not ordinary untouched writing.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="RAID attack results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Eligible AI passages detected after transformation
                      </caption>
                      <thead>
                        <tr>
                          <th>Transformation</th>
                          <th>Live threshold</th>
                          <th>RAID comparison</th>
                        </tr>
                      </thead>
                      <tbody>
                        <ResultRows rows={raidAttackResults} />
                      </tbody>
                    </table>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026 on RAID’s labeled training
                    release, not its hidden test set or official leaderboard.
                    RAID’s comparison threshold was selected independently
                    within each domain, following its default protocol. At Fast
                    1.0's recorded production threshold, clean human clearance
                    was 99.90%; across transformed human passages it ranged from
                    97.79% to 100%. Passages shorter than Stylome’s
                    200-character input floor are preserved in the audit but
                    excluded from the product-rate tables above.
                  </p>
                </div>
              </details>
            </section>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · PAN at CLEF 2025
                    </span>
                    <strong>PAN 2025</strong>
                    <span>
                      Human and AI-generated essays, fiction, and news from an
                      independent detection challenge.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="PAN 2025 highlights"
                  >
                    <BenchmarkMetric
                      label="Human writing cleared"
                      value="99.84%"
                      context="1,275 of 1,277"
                    />
                    <BenchmarkMetric
                      label="AI-generated detected"
                      value="95.59%"
                      context="2,210 of 2,312"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    The complete labeled validation set, scored at Stylome’s
                    public threshold with no threshold fitting on PAN.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      <a
                        href="https://pan.webis.de/clef25/pan25-web/generated-content-analysis.html"
                        target="_blank"
                        rel="noreferrer"
                      >
                        PAN 2025
                      </a>{" "}
                      is an academic shared task for distinguishing human
                      writing from generated text. Its validation set spans
                      essays, fiction, and news, with output from 22 model
                      families and variants.
                    </p>
                    <p>
                      Every one of its 3,589 passages is long enough for
                      Stylome’s scanner. None has full-text or prefix overlap
                      with the final model training corpus.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>1,275 of 1,277</strong> human passages were
                      correctly left unflagged.
                    </p>
                    <p>
                      <strong>2,210 of 2,312</strong> AI-generated passages were
                      detected.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="PAN 2025 results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Stylome 1.0 Fast on PAN 2025 by writing type
                      </caption>
                      <thead>
                        <tr>
                          <th>Writing type</th>
                          <th>Human cleared</th>
                          <th>AI generated detected</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <th scope="row">Essays</th>
                          <td>100% (132/132)</td>
                          <td>98.87% (527/533)</td>
                        </tr>
                        <tr>
                          <th scope="row">Fiction</th>
                          <td>99.78% (926/928)</td>
                          <td>92.08% (837/909)</td>
                        </tr>
                        <tr>
                          <th scope="row">News</th>
                          <td>100% (217/217)</td>
                          <td>97.24% (846/870)</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026 on PAN’s labeled validation
                    set, not its hidden official test set. Detection on
                    Llama-3.1-8B-Instruct was 68.99%; the aggregate results
                    above span all 22 model families and variants in the
                    validation set.
                  </p>
                </div>
              </details>
            </section>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · ELLIPSE Corpus
                    </span>
                    <strong>ELLIPSE</strong>
                    <span>
                      Standardized-test essays written by English learners in
                      grades 8–12.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="ELLIPSE highlights"
                  >
                    <BenchmarkMetric
                      label="Human essays cleared"
                      value="99.94%"
                      context="1,712 of 1,713"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    A frozen student-writing check drawn from an established
                    public learner-English corpus.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      The open-source{" "}
                      <a
                        href="https://doi.org/10.1075/ijlcr.22026.cro"
                        target="_blank"
                        rel="noreferrer"
                      >
                        ELLIPSE corpus
                      </a>{" "}
                      contains roughly 6,500 essays written during statewide
                      testing by English learners in grades 8–12.
                    </p>
                    <p>
                      After removing material inherited by an earlier model
                      lineage, we split the remaining 3,425 eligible essays with
                      a fixed random seed: 1,712 for training and 1,713 for this
                      frozen evaluation.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>1,712 of 1,713</strong> human essays were
                      correctly left unflagged.
                    </p>
                    <p>
                      <strong>1 of 1,713</strong> received an incorrect
                      AI-generated result.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="ELLIPSE results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Stylome 1.0 Fast on the frozen ELLIPSE half
                      </caption>
                      <thead>
                        <tr>
                          <th>Human essays</th>
                          <th>Correctly cleared</th>
                          <th>False positives</th>
                          <th>Clearance rate</th>
                          <th>False-positive rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>1,713</td>
                          <td>1,712</td>
                          <td>1</td>
                          <td>99.94%</td>
                          <td>0.06%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026 at Stylome’s public
                    threshold. The held half has zero full-text or prefix
                    overlap with final model training. This is a Stylome-defined
                    frozen split of ELLIPSE, not an official detector benchmark
                    split published by the corpus authors.
                  </p>
                </div>
              </details>
            </section>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · PERSUADE 2.0
                    </span>
                    <strong>PERSUADE</strong>
                    <span>
                      Argumentative essays written by United States students in
                      grades 6–12.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="PERSUADE highlights"
                  >
                    <BenchmarkMetric
                      label="Human essays cleared"
                      value="100%"
                      context="893 of 893"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    A frozen student-writing check drawn from a large public
                    corpus of persuasive essays.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      The open-source{" "}
                      <a
                        href="https://doi.org/10.1016/j.asw.2024.100865"
                        target="_blank"
                        rel="noreferrer"
                      >
                        PERSUADE 2.0 corpus
                      </a>{" "}
                      contains more than 25,000 argumentative essays written by
                      students in grades 6–12 across 15 prompts.
                    </p>
                    <p>
                      After removing material inherited by an earlier model
                      lineage, we split our 1,785-essay eligible sample with a
                      fixed random seed: 892 for training and 893 for this
                      frozen evaluation.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>893 of 893</strong> human essays were correctly
                      left unflagged.
                    </p>
                    <p>
                      <strong>0 of 893</strong> received an incorrect
                      AI-generated result.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="PERSUADE results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Stylome 1.0 Fast on the frozen PERSUADE half
                      </caption>
                      <thead>
                        <tr>
                          <th>Human essays</th>
                          <th>Correctly cleared</th>
                          <th>False positives</th>
                          <th>Clearance rate</th>
                          <th>False-positive rate</th>
                          <th>95% upper bound</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>893</td>
                          <td>893</td>
                          <td>0</td>
                          <td>100%</td>
                          <td>0%</td>
                          <td>0.34%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026 at Stylome’s public
                    threshold. The held half has zero full-text or prefix
                    overlap with final model training. With no observed errors,
                    0.34% is the conservative rule-of-three upper bound. This is
                    a Stylome-defined frozen split, not an official detector
                    benchmark split published by the corpus authors.
                  </p>
                </div>
              </details>
            </section>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · Pangram Labs
                    </span>
                    <strong>EditLens</strong>
                    <span>
                      Human-written, AI-edited, and AI-generated text across
                      three test sections.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="Public threshold highlights"
                  >
                    <BenchmarkMetric
                      label="Human writing cleared"
                      value="99.93%"
                      context="6,064 of 6,068"
                    />
                    <BenchmarkMetric
                      label="AI-generated detected"
                      value="99.46%"
                      context="5,899 of 5,931"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    Evaluated on raw text with Stylome 1.0 Fast. These
                    measurements are not attributed to the newer Stylome 1.0
                    standard model.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      <a
                        href="https://github.com/pangramlabs/EditLens"
                        target="_blank"
                        rel="noreferrer"
                      >
                        EditLens
                      </a>{" "}
                      is a dataset from Pangram Labs built to study how much AI
                      has changed a piece of writing. Unlike a simple
                      human-or-AI test, these results use three labels:{" "}
                      <strong>human written</strong>, <strong>AI edited</strong>
                      , and <strong>AI generated</strong>.
                    </p>
                    <p>
                      That middle category matters. Lightly or heavily edited
                      human text is a different problem from text written
                      entirely by a model, and the results below show that it is
                      also the harder one.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>6,064 of 6,068</strong> human passages were
                      correctly left unflagged.
                    </p>
                    <p>
                      <strong>5,899 of 5,931</strong> fully generated passages
                      were detected.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="EditLens public threshold results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Performance at Stylome’s public threshold
                      </caption>
                      <thead>
                        <tr>
                          <th>Test section</th>
                          <th>Human writing cleared</th>
                          <th>False positives</th>
                          <th>AI edited detected</th>
                          <th>AI generated detected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editLensResults.map((result) => (
                          <tr key={result.split}>
                            <th scope="row">{result.split}</th>
                            <td>{result.humanCleared}</td>
                            <td>{result.falsePositives}</td>
                            <td>{result.editedDetected}</td>
                            <td>{result.generatedDetected}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="benchmark-heading">
                    <span className="metadata-line">Research benchmark</span>
                    <h3>Three labels make this a harder test</h3>
                    <p>
                      The live scanner asks whether the evidence is strong
                      enough to flag a passage. EditLens also asks the model to
                      distinguish AI-edited writing from fully AI-generated
                      writing. A random choice among three balanced labels would
                      score about 33%.
                    </p>
                  </div>
                  <div
                    className="table-wrap editlens-table"
                    role="region"
                    aria-label="EditLens three-class results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>
                        Three-class performance for Stylome 1.0 Fast on EditLens
                      </caption>
                      <thead>
                        <tr>
                          <th>Test section</th>
                          <th>Passages</th>
                          <th>Overall correct</th>
                          <th>Balanced score</th>
                          <th>Human found</th>
                          <th>AI edited found</th>
                          <th>AI generated found</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editLensResults.map((result) => (
                          <tr key={result.split}>
                            <th scope="row">
                              {result.split}
                              <small>{result.description}</small>
                            </th>
                            <td>{result.passages}</td>
                            <td>{result.accuracy}</td>
                            <td>{result.macroF1}</td>
                            <td>{result.humanRecall}</td>
                            <td>{result.editedRecall}</td>
                            <td>{result.generatedRecall}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="metric-explainer">
                    <p>
                      <strong>Overall correct</strong> is the share assigned the
                      right label. <strong>Balanced score</strong> is macro-F1:
                      it gives each of the three labels equal weight. The final
                      three columns show how often Stylome found each class.
                    </p>
                    <p>
                      The three-way scores complement the public-threshold
                      results: together they show Stylome 1.0 Fast's binary
                      detection strength and its separate performance on
                      AI-edited writing across each official split.
                    </p>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026. Pangram’s dataset release
                    contains 28 training/validation overlaps in Standard test
                    and 27 in Llama (about 0.45% of each); Enron has none.
                    Removing those rows changes reconstructed accuracy by less
                    than 0.04 percentage points. Official-split results are
                    shown here so they remain comparable and reproducible.
                  </p>
                </div>
              </details>
            </section>
            <section className="content-section benchmark-section">
              <details className="benchmark-report">
                <summary>
                  <span className="benchmark-identity">
                    <span className="metadata-line">
                      Stylome 1.0 Fast · Liang et al.
                    </span>
                    <strong>TOEFL91</strong>
                    <span>
                      Human essays written by people learning English as an
                      additional language.
                    </span>
                  </span>
                  <span
                    className="benchmark-summary-metrics"
                    aria-label="TOEFL91 highlights"
                  >
                    <BenchmarkMetric
                      label="Human essays cleared"
                      value="94.51%"
                      context="86 of 91"
                    />
                  </span>
                  <span className="benchmark-toggle">Full results</span>
                </summary>
                <div className="benchmark-body">
                  <p className="benchmark-context">
                    A deliberately difficult fairness check for false positives
                    on human writing by non-native English speakers.
                  </p>
                  <div className="dataset-intro">
                    <p>
                      <a
                        href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10382961/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        TOEFL91
                      </a>{" "}
                      contains 91 human-written TOEFL essays used by Liang and
                      colleagues to study detector bias against non-native
                      English writers.
                    </p>
                    <p>
                      Their 2023 study reported that seven contemporary
                      detectors falsely flagged an average of 61.3% of these
                      essays. Stylome 1.0 Fast cleared 94.51% of the same
                      essays, reducing the false-positive rate to 5.49%.
                    </p>
                  </div>
                  <div className="aggregate-counts">
                    <p>
                      <strong>86 of 91</strong> human essays were correctly left
                      unflagged.
                    </p>
                    <p>
                      <strong>5 of 91</strong> received an incorrect
                      AI-generated result.
                    </p>
                  </div>
                  <div
                    className="table-wrap threshold-table"
                    role="region"
                    aria-label="TOEFL91 results"
                    tabIndex={0}
                  >
                    <table>
                      <caption>Stylome 1.0 Fast on TOEFL91</caption>
                      <thead>
                        <tr>
                          <th>Human essays</th>
                          <th>Correctly cleared</th>
                          <th>False positives</th>
                          <th>False-positive rate</th>
                          <th>95% interval</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>91</td>
                          <td>86</td>
                          <td>5</td>
                          <td>5.49%</td>
                          <td>1.81–12.36%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="fine evaluation-note">
                    Evaluation completed July 2026 at Stylome’s public
                    threshold. The 91 essays have zero full-text or prefix
                    overlap with model training. This small, single-register
                    sample should not be read as a complete measure of
                    performance for multilingual or non-native English writing.
                  </p>
                </div>
              </details>
            </section>
          </>
        )}
      </div>
      <section className="content-section">
        <h2>How to read a result</h2>
        <p>
          Stylome 1.2 Large distinguishes authorship composition: human writing,
          human writing with an AI contribution, and fully machine-derived text.
          These classes are not confidence levels. Low, medium, or high
          confidence reports relative KNN support separately; it is not a
          probability of correctness. A result is not proof of authorship.
        </p>
        <div
          className="table-wrap"
          role="region"
          aria-label="Result meaning table"
          tabIndex={0}
        >
          <table>
            <caption>What each result means</caption>
            <thead>
              <tr>
                <th>Result</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              {resultMeanings.map(([result, meaning]) => (
                <tr key={result}>
                  <th
                    scope="row"
                    className={
                      result === "Human written"
                        ? "result-label-human"
                        : result === "AI edited"
                          ? "result-label-mixed"
                          : "result-label-ai"
                    }
                  >
                    {result}
                  </th>
                  <td>{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="content-section">
        <h2>What we test</h2>
        <p>
          A model can look excellent overall while struggling with a particular
          kind of writing. Each report will therefore show separate results for
          essays, fiction, web articles, social posts, conversations, and
          writing by people who learned English as an additional language.
        </p>
        <div className="split transparency-checks">
          <article>
            <h3>For every model</h3>
            <ul className="check-list">
              <li>Overall detection results</li>
              <li>False positives on human writing</li>
              <li>Results for each kind of writing</li>
              <li>Performance at different passage lengths</li>
              <li>Test size and evaluation date</li>
            </ul>
          </article>
          <article>
            <h3>Before the numbers appear</h3>
            <p>
              We test on writing kept separate from model training and check for
              accidental duplicates. That gives the published results a fairer
              chance of reflecting new, unseen text.
            </p>
          </article>
        </div>
      </section>
      <section className="content-section">
        <h2>Comparing model versions</h2>
        <p>
          Each report lists the benchmarks completed for that model. Stylome 1.2
          Large currently has EditLens, PAN 2025, StoryScope, ELLIPSE, PERSUADE,
          and TOEFL91 results. Compare models on the same test population and at
          matched false-positive operating points.
        </p>
        <div
          className="table-wrap"
          role="region"
          aria-label="Model comparison table"
          tabIndex={0}
        >
          <table>
            <caption>Available Stylome models</caption>
            <thead>
              <tr>
                <th>Model</th>
                <th>Designed for</th>
                <th>Status</th>
                <th>Results</th>
              </tr>
            </thead>
            <tbody>
              {transparencyModels.map((model) => (
                <tr key={model.id}>
                  <th scope="row">{model.name}</th>
                  <td>{model.purpose}</td>
                  <td>{model.availability}</td>
                  <td>{model.reportStatus}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="content-section">
        <h2>Known limits</h2>
        <p>
          Heavily edited or translated passages can be harder to classify, as
          can templates, highly unusual writing styles, and text near the
          200-character minimum. Stylome evaluates the passage itself; it does
          not identify who wrote it.
        </p>
      </section>
      <details className="technical-notes">
        <summary>Technical reporting details</summary>
        <div className="split">
          <article>
            <h2>Included with each report</h2>
            <ul className="check-list">
              <li>Frozen holdout provenance and SHA-256 overlap audit</li>
              <li>Matched-FPR model comparison with confidence intervals</li>
              <li>Per-register and per-source tail metrics</li>
              <li>TOEFL, Nairaland, and ELLIPSE ESL audits</li>
              <li>
                Calibration method, sample counts, model version, and evaluation
                date
              </li>
            </ul>
          </article>
          <article>
            <h2>Statistics kept separate</h2>
            <dl className="definitions">
              <dt>Calibrated false-positive rate</dt>
              <dd>The measured error rate on a fixed evaluation set.</dd>
              <dt>Live bounty findings</dt>
              <dd>
                Examples found by people actively searching for mistakes. Useful
                for improving the model, but not an overall error-rate estimate.
              </dd>
            </dl>
          </article>
        </div>
      </details>
      <div className="content-section">
        <p>
          <Link to="/methodology">Read the evaluation methodology</Link> for the
          rules governing holdouts, overlap audits, calibration, and comparisons
          between models.
        </p>
      </div>
    </Page>
  );
}

const CHROME_STORE_URL =
  "https://chromewebstore.google.com/detail/stylome/dapkhebnopgmkajnndppfjlnendelmbi";

function InstallButton({
  placement,
  className = "button primary",
}: {
  placement: string;
  className?: string;
}) {
  return (
    <a
      href={CHROME_STORE_URL}
      className={className}
      onClick={() => trackEvent(STORE_CLICK_EVENT, { placement })}
    >
      Add to Chrome
    </a>
  );
}

const extensionFeatures = [
  [
    "Verdicts where you read",
    "Posts, comments, and articles on Reddit and 400+ other sites get a small Stylome badge. It shows Human written, AI edited, or AI generated.",
  ],
  [
    "Any text, one right-click",
    "Select at least 200 characters on any page and choose “Analyze selection with Stylome.”",
  ],
  [
    "You decide what is sent",
    "Automatic scanning is off until you turn it on. Until then, text goes to Stylome only when you click a badge or analyze a selection.",
  ],
];

const extensionPermissions = [
  ["Supported sites", "Find posts and comments and show badges on them."],
  [
    "Active tab and scripting",
    "Show a result after you use the right-click menu.",
  ],
  ["Context menu", "Add “Analyze selection with Stylome.”"],
  ["Identity", "Sign in to your Stylome account."],
  [
    "Storage and alarms",
    "Keep your settings and local statistics; retry after a rate limit.",
  ],
];

function ExtensionPage() {
  return (
    <Page title="Stylome for Chrome">
      <p className="lede">
        Stylome marks the posts, comments, and articles written by AI, right on
        the page, as you browse Reddit and more than 400 other sites.
      </p>
      <div className="install-actions">
        <InstallButton placement="extension_hero" />
        <p className="fine">
          14-day trial, no card required. Then $19 a month or $190 a year.
        </p>
      </div>
      <section className="feature-grid" aria-label="What the extension does">
        {extensionFeatures.map(([title, body]) => (
          <article key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </article>
        ))}
      </section>
      <section className="content-section">
        <h2>What the extension sends</h2>
        <p>
          The extension sends text to Stylome over HTTPS only when you click a
          badge, analyze a selection, or turn on automatic scanning. It sends at
          most the first 10,000 characters of a passage. Stylome keeps the
          passage and its result in your account history. The extension does not
          read your browsing history, has no advertising telemetry, and does not
          sell data. <Link to="/privacy">Read the privacy notice</Link>.
        </p>
      </section>
      <section className="content-section">
        <h2>Permissions</h2>
        <dl className="permission-list">
          {extensionPermissions.map(([name, use]) => (
            <div key={name}>
              <dt>{name}</dt>
              <dd>{use}</dd>
            </div>
          ))}
        </dl>
        <p>
          <Link to="/supported-sites">Search the supported sites</Link>.
        </p>
      </section>
    </Page>
  );
}

function Welcome() {
  return (
    <Page title="Stylome is installed">
      <p className="lede">Three steps and you are ready.</p>
      <ol className="welcome-steps">
        <li>
          <h2>Pin Stylome</h2>
          <p>
            Click the puzzle-piece icon in the Chrome toolbar, then the pin next
            to Stylome.
          </p>
        </li>
        <li>
          <h2>Sign in</h2>
          <p>
            Click the Stylome icon and sign in. Your 14-day trial starts with
            your first scan. No card is required.
          </p>
        </li>
        <li>
          <h2>Scan something</h2>
          <p>
            Open Reddit and click a Stylome badge on a post, or select text on
            any page, right-click, and choose “Analyze selection with Stylome.”
            To scan without clicking, turn on automatic scanning in the Stylome
            popup.
          </p>
        </li>
      </ol>
      <p className="welcome-links">
        <Link to="/supported-sites">Supported sites</Link> ·{" "}
        <Link to="/transparency">How accurate is Stylome?</Link> ·{" "}
        <a href="mailto:support@stylome.io">Support</a>
      </p>
    </Page>
  );
}

function Pricing() {
  return (
    <Page title="Pricing">
      <p className="lede">
        Choose free manual scans, Premium browser extension access, or prepaid
        API access.
      </p>
      <section className="pricing">
        <article className="featured">
          <span>Available now</span>
          <h2>Free</h2>
          <strong>$0</strong>
          <p className="plan-description">
            Scan manually in the paste box for free, without usage limits.
            Automated or abusive traffic may be restricted.
          </p>
          <ul>
            <li>Manual scans up to 10,000 characters</li>
            <li>Three authorship classes</li>
            <li>Authorship class and relative confidence</li>
            <li>Public document receipt</li>
          </ul>
          <Link to="/" className="button primary">
            Scan text
          </Link>
        </article>
        <article className="featured">
          <span>Personal plan</span>
          <h2>Premium</h2>
          <strong>
            $19 <small>/ month</small>
          </strong>
          <p className="annual-price">or $190 yearly — save $38</p>
          <ul>
            <li>14-day free trial, no card required</li>
            <li>
              <Link to="/supported-sites">Over 400 sites supported</Link>
            </li>
            <li>Rolling 24-hour analysis allowance</li>
            <li>In-page verdicts</li>
            <li>Account history and local statistics</li>
          </ul>
          <div className="plan-actions">
            <InstallButton placement="pricing" />
            <Link to="/account" className="button secondary">
              Choose a plan
            </Link>
          </div>
        </article>
      </section>
      <section
        className="content-section api-pricing"
        aria-labelledby="api-pricing-title"
      >
        <h2 id="api-pricing-title">Developer API</h2>
        <p>
          Prepaid access to both models, billed separately from the personal
          extension plan. The API is in early alpha.
        </p>
        <div className="table-wrap">
          <table>
            <caption>USD per 1 million normalized input characters</caption>
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Stylome 1.0 Large</th>
                <td>$5.00</td>
              </tr>
              <tr>
                <th scope="row">Stylome 1.2 Large</th>
                <td>$5.00</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Add $5–$1,000 in credit at a time. Applicable tax is added at
          checkout. Successful calls, including cached results, are charged.
          Failed calls and idempotent replays add no charge.
        </p>
        <div className="api-pricing-actions">
          <Link to="/account#api-credits" className="button primary">
            Add API credit
          </Link>
          <Link to="/developers" className="button">
            API documentation
          </Link>
        </div>
      </section>
    </Page>
  );
}

function SupportedSitesPage() {
  return (
    <Page title="Supported sites">
      <p className="lede">
        Stylome’s extension recognizes hundreds of publishing platforms,
        communities, newsrooms, review sites, and research libraries. Search the
        live extension catalog before you install.
      </p>
      <div className="install-actions">
        <InstallButton placement="supported_sites" />
      </div>
      <SupportedSites />
    </Page>
  );
}

function Developers() {
  return (
    <Page title="Developer API">
      <p className="lede">
        The Stylome API is in early alpha. Fund your account with prepaid credit
        to use either model. API access is billed separately from the personal
        extension plan. Endpoints and models may change during alpha without a
        guaranteed notice period.
      </p>
      <section className="content-section">
        <h2>API documentation</h2>
        <p>
          Explore the endpoints, request options, and response schemas in our
          interactive API reference.
        </p>
        <a className="button primary" href="https://stylome.io/api/docs">
          Open API documentation
        </a>
      </section>
      <section className="content-section">
        <h2>Select a model</h2>
        <p>
          Use <code>stylome-1.2-large</code> for human-written, AI-edited, or
          AI-generated classifications and low, medium, or high relative KNN
          confidence. Confidence is not a probability. Select <code>0.01%</code>{" "}
          (the default) or <code>0.1%</code> with <code>operating_point</code>.
        </p>
        <p>
          <code>stylome-1.0-large</code> remains available with its separate
          score-based response. It does not return KNN confidence or accept an
          operating-point option. Unsupported model IDs return <code>422</code>{" "}
          before inference. All choices use the same rate limit and usage unit.
        </p>
        <p>
          For 1.2, inputs must contain 200–10,000 characters after whitespace
          normalization. Unicode characters are preserved. Text is never
          silently truncated. Responses return the authorship class and relative
          confidence for your selected operating point, with model, evaluation,
          and response version identifiers. Numerical detector scores and
          internal diagnostics are not returned.
        </p>
        <p>
          Use the published dataset selection, exclusions, preprocessing,
          evaluation version, operating point, and class mapping to reproduce
          fixed-point false-positive rates, detection recall, and ternary
          accuracy. Confidence also supports tier coverage and accuracy checks.
          Operating points are calibration targets, not guaranteed error rates
          on other datasets. Continuous-score analyses such as ROC curves,
          AUROC, and custom threshold sweeps are not available through this
          public response.
        </p>
        <p>
          An idempotency key replays the same request; reusing it with different
          text, model, or operating point returns <code>409</code>.
        </p>
      </section>
      <section
        className="content-section"
        aria-labelledby="zero-retention-heading"
      >
        <h2 id="zero-retention-heading">Zero customer-content retention</h2>
        <p>
          Choose <strong>Zero customer-content retention</strong> when you
          commission an API key. The choice is fixed for that key, so an
          application cannot silently fall back to standard retention when it
          omits a request option. Existing keys use standard retention.
        </p>
        <p>
          Zero-retention keys support <code>stylome-1.2-large</code> and{" "}
          <code>stylome-1.0-large</code>. Do not send{" "}
          <code>Idempotency-Key</code>: replay requires stored request-derived
          data and returns <code>400</code> for these keys.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Not retained</th>
                <th scope="col">Operational metadata retained</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Submitted text, excerpts, content hashes, results, receipts,
                  cache entries, and idempotency records.
                </td>
                <td>
                  Key retention mode, request ID, timestamp, model and scoring
                  contract, HTTP status, latency, normalized character count,
                  USD charge, and rate-limit state. Character counts are billing
                  metadata only.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="muted">
          The response still returns the result and content hash to the caller,
          but Stylome does not store them. See the{" "}
          <Link to="/privacy">privacy policy</Link> for the complete service
          description.
        </p>
      </section>
      <section className="content-section" aria-labelledby="rate-limit-heading">
        <h2 id="rate-limit-heading">120 requests per minute, per account</h2>
        <p>
          Every key owned by an account draws from the same fixed one-minute
          quota. Successful requests, failed scoring attempts, and idempotent
          replays count after authentication and text validation. When the quota
          is exhausted, the API returns <code>429 Too Many Requests</code> with{" "}
          <code>Retry-After</code> set to the seconds remaining in the current
          window.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Header</th>
                <th scope="col">Meaning</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <code>RateLimit-Policy</code>
                </td>
                <td>Quota and one-minute window for the account policy.</td>
              </tr>
              <tr>
                <td>
                  <code>RateLimit</code>
                </td>
                <td>Requests remaining and seconds until the window resets.</td>
              </tr>
              <tr>
                <td>
                  <code>X-RateLimit-Limit</code>
                </td>
                <td>Compatibility field containing the 120-request quota.</td>
              </tr>
              <tr>
                <td>
                  <code>X-RateLimit-Remaining</code>
                </td>
                <td>Compatibility field containing requests remaining.</td>
              </tr>
              <tr>
                <td>
                  <code>X-RateLimit-Reset</code>
                </td>
                <td>
                  Compatibility field containing the reset time as a Unix
                  timestamp.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="muted">
          The request-rate window is separate from prepaid credit. Both public
          models cost $5 per million normalized input characters. Fund your API
          balance through Stripe on your account page. Successful requests,
          including cache hits, are charged; failures and idempotent replays do
          not add a charge. A request without enough available credit returns
          402.
        </p>
      </section>
      <section
        className="content-section"
        aria-labelledby="api-operations-heading"
      >
        <h2 id="api-operations-heading">Operate your integration</h2>
        <p>
          Verify a key with <code>GET /api/v1/key</code>. Keys with{" "}
          <code>usage:read</code> can query <code>GET /api/v1/usage</code> by
          time range, model, or key. Keys with <code>account:read</code> can
          inspect <code>GET /api/v1/limits</code> without consuming the scoring
          request window. Existing keys retain their original permissions;
          commission a replacement to add read access.
        </p>
        <p>
          Every API attempt returns <code>X-Request-ID</code>, including errors.
          Scoring also returns <code>X-Operation-ID</code>, which stays the same
          when an idempotency key replays a result. Errors include a stable code
          and retry guidance. Keep request IDs for support; avoid logging secret
          keys, submitted text, or private receipt capabilities.
        </p>
        <p>
          Use a new idempotency key for each logical standard-retention
          operation and reuse it on retries. Completed results replay for 24
          hours. A processing conflict returns 409 with <code>Retry-After</code>
          . Zero-content keys cannot replay requests. Do not automatically retry
          a scoring POST without an idempotency key.
        </p>
        <p>
          Scoring admission and inference have a 110-second deadline; allow 150
          seconds in your client for transport and finalization. The service
          admits eight developer scoring calls per worker. Follow{" "}
          <code>Retry-After</code> on 429 and use bounded exponential backoff
          with jitter for retryable failures. A network failure can occur after
          processing, so preserve the idempotency key.
        </p>
        <p>
          <a href="/api/v1/status">Service status</a> checks API/database
          reachability and inference configuration. It does not run a detector
          canary or guarantee inference availability.
          <a href="/api/docs"> Interactive API documentation</a> includes model
          capabilities, request headers, error shapes, and read endpoints.
        </p>
      </section>
    </Page>
  );
}

function Login() {
  const error = new URLSearchParams(location.search).get("error");
  const returnTo =
    new URLSearchParams(location.search).get("return_to") || "/account";
  const messages: Record<string, string> = {
    google_not_configured:
      "Google sign-in is not configured on this deployment yet.",
    access_denied: "Google sign-in was cancelled.",
    oauth_callback_failed:
      "Google could not complete sign-in. Please try again.",
    missing_code: "Google returned without an authorization code.",
    session_expired: "Your session expired. Sign in again to continue.",
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  useEffect(() => {
    fetch("/api/v1/config")
      .then((response) => response.json())
      .then((config) =>
        setPasswordEnabled(Boolean(config.password_auth_enabled)),
      )
      .catch(() => setPasswordEnabled(false));
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setFormError("");
    try {
      const response = await fetch("/api/v1/auth/password/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password, return_to: returnTo }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          body?.error?.message || "The email or password is incorrect.",
        );
      location.replace(body.return_to);
    } catch (caught) {
      setFormError(
        caught instanceof Error ? caught.message : "Sign-in failed.",
      );
      setBusy(false);
    }
  }
  return (
    <Page title="Sign in to Stylome">
      <div className="auth-card">
        {error && (
          <p className="alert error" role="alert">
            {messages[error] || "Sign-in could not be completed."}
          </p>
        )}
        <a className="button google" href={googleAuthUrl(returnTo)}>
          <span>G</span> Continue with Google
        </a>
        {passwordEnabled && (
          <>
            <div className="auth-divider">
              <span>or use email</span>
            </div>
            <form className="auth-form" onSubmit={submit}>
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {formError && (
                <p className="alert error" role="alert">
                  {formError}
                </p>
              )}
              <button className="button primary" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
            <p className="auth-links">
              <Link to={`/register?return_to=${encodeURIComponent(returnTo)}`}>
                Create an account
              </Link>
              <Link to="/forgot-password">Forgot password?</Link>
            </p>
          </>
        )}
      </div>
    </Page>
  );
}

function EmailRequestPage({ reset = false }: { reset?: boolean }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const turnstileContainer = useRef<HTMLDivElement>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const token = await turnstileToken(
        turnstileContainer.current!,
        reset ? "password_reset" : "register",
      );
      const response = await fetch(
        reset
          ? "/api/v1/auth/password/reset/request"
          : "/api/v1/auth/password/register",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, turnstile_token: token }),
        },
      );
      const body = await response.json().catch(() => null);
      if (body?.error?.code === "google_sso_required")
        throw new Error(
          "Gmail addresses use Continue with Google. No password is needed.",
        );
      if (!response.ok)
        throw new Error(
          body?.error?.message || "The request could not be completed.",
        );
      setNotice(
        reset
          ? "If that address has a password account, a reset link is on its way."
          : "Check your email for a verification link. You will choose your password after verification.",
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The request could not be completed.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Page title={reset ? "Reset your password" : "Create your Stylome account"}>
      <div className="auth-card">
        <p>
          {reset
            ? "Enter your account email. Reset links expire after 30 minutes."
            : "First, verify your email address. Verification links expire after 24 hours."}
        </p>
        {!reset && (
          <p className="auth-guidance">
            <strong>Using Gmail?</strong> Return to sign in and choose Continue
            with Google.
          </p>
        )}
        <form className="auth-form" onSubmit={submit}>
          <label htmlFor="request-email">Email</label>
          <input
            id="request-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div ref={turnstileContainer} />
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
          {notice && (
            <p className="alert success" role="status">
              {notice}
            </p>
          )}
          <button className="button primary" disabled={busy || Boolean(notice)}>
            {busy
              ? "Sending…"
              : reset
                ? "Send reset link"
                : "Send verification link"}
          </button>
        </form>
        <p className="auth-links">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </Page>
  );
}

function PasswordCompletePage({ reset = false }: { reset?: boolean }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const token =
    typeof location === "undefined"
      ? ""
      : new URLSearchParams(location.hash.slice(1)).get("token") || "";
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    try {
      const turnstile = await turnstileToken(
        turnstileContainer.current!,
        reset ? "password_reset_complete" : "register_complete",
      );
      const response = await fetch(
        reset
          ? "/api/v1/auth/password/reset/complete"
          : "/api/v1/auth/password/register/complete",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            token,
            password,
            turnstile_token: turnstile,
          }),
        },
      );
      const body = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          body?.error?.message || "This link is invalid or expired.",
        );
      history.replaceState({}, "", location.pathname);
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The password could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (done)
    return (
      <Page title={reset ? "Password reset" : "Email verified"}>
        <div className="auth-card">
          <p className="alert success" role="status">
            Your password is ready. Sign in to continue.
          </p>
          <Link className="button primary" to="/login">
            Sign in
          </Link>
        </div>
      </Page>
    );
  return (
    <Page
      title={
        reset ? "Choose a new password" : "Email verified. Choose a password."
      }
    >
      <div className="auth-card">
        {!token && (
          <p className="alert error" role="alert">
            This link has no token. Request a new email.
          </p>
        )}
        <p>
          Use 15–128 characters. Spaces and Unicode characters are allowed.
          Known compromised passwords are rejected.
        </p>
        <form className="auth-form" onSubmit={submit}>
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            minLength={15}
            maxLength={128}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <label htmlFor="confirm-password">Confirm password</label>
          <input
            id="confirm-password"
            type={show ? "text" : "password"}
            autoComplete="new-password"
            minLength={15}
            maxLength={128}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <label className="show-password">
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
            />{" "}
            Show passwords
          </label>
          <div ref={turnstileContainer} />
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
          <button className="button primary" disabled={busy || !token}>
            {busy ? "Saving…" : reset ? "Reset password" : "Create account"}
          </button>
        </form>
      </div>
    </Page>
  );
}

function ApiKeyManager() {
  const [keys, setKeys] = useState<any[]>([]);
  const [usage, setUsage] = useState<any>(null);
  const [name, setName] = useState("");
  const [retentionMode, setRetentionMode] = useState<
    "standard" | "zero_content"
  >("standard");
  const [scopes, setScopes] = useState<string[]>(["score:create"]);
  const [expiryDays, setExpiryDays] = useState("never");
  const [issued, setIssued] = useState("");
  const [issuedRetention, setIssuedRetention] = useState<
    "standard" | "zero_content"
  >("standard");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      const [keysResponse, usageResponse] = await Promise.all([
        fetch("/api/v1/api-keys"),
        fetch("/api/v1/api-usage"),
      ]);
      if (!keysResponse.ok || !usageResponse.ok)
        throw new Error("API key information could not be loaded.");
      const keysBody = await keysResponse.json();
      setKeys(keysBody.items);
      setUsage(await usageResponse.json());
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : "API key information could not be loaded.",
      );
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function createKey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setIssued("");
    setCopyState("idle");
    try {
      const response = await fetch("/api/v1/api-keys", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name,
          retention_mode: retentionMode,
          scopes,
          expires_at:
            expiryDays === "never"
              ? null
              : new Date(
                  Date.now() + Number(expiryDays) * 86_400_000,
                ).toISOString(),
        }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(
          body?.error?.message ||
            `API key creation failed (${response.status}).`,
        );
      if (!body) throw new Error("The server returned an unreadable response.");
      setIssued(body.key);
      setIssuedRetention(body.credential.retention_mode);
      setName("");
      setRetentionMode("standard");
      setScopes(["score:create"]);
      setExpiryDays("never");
      await load();
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "API key creation failed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function copyIssuedKey() {
    try {
      await navigator.clipboard.writeText(issued);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  async function revoke(publicId: string, keyName: string) {
    if (!confirm(`Revoke ${keyName}? Applications using it will stop working.`))
      return;
    setError("");
    const response = await fetch(`/api/v1/api-keys/${publicId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      setError(
        body?.error?.message ||
          `API key revocation failed (${response.status}).`,
      );
      return;
    }
    await load();
  }

  return (
    <section
      id="api-keys"
      className="api-key-panel"
      aria-labelledby="api-key-heading"
    >
      <div className="api-key-heading">
        <div>
          <h2 id="api-key-heading">API keys</h2>
          <p>
            Choose only the permissions your application needs. Keep secret keys
            on your server.
          </p>
        </div>
        {usage && (
          <dl className="api-usage" aria-label="API usage in the last 30 days">
            <div>
              <dt>Requests</dt>
              <dd>{usage.requests.toLocaleString()}</dd>
            </div>
            <div>
              <dt>Standard-key input characters</dt>
              <dd>{usage.input_characters.toLocaleString()}</dd>
            </div>
          </dl>
        )}
      </div>

      <form className="api-key-form" onSubmit={createKey}>
        <label htmlFor="api-key-name">Key name</label>
        <input
          id="api-key-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
          placeholder="Production server"
          required
        />
        <fieldset className="api-key-retention">
          <legend>Permissions</legend>
          {[
            [
              "score:create",
              "Score text",
              "Analyze passages with the public models.",
            ],
            [
              "usage:read",
              "Read usage",
              "Read request counts across this account's API keys.",
            ],
            [
              "account:read",
              "Read limits",
              "Read this account's prepaid balance and request limits.",
            ],
          ].map(([scope, label, description]) => (
            <label key={scope}>
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={(event) =>
                  setScopes(
                    event.target.checked
                      ? [...scopes, scope]
                      : scopes.filter((value) => value !== scope),
                  )
                }
              />
              <span>
                <strong>{label}</strong>
                <small>{description}</small>
              </span>
            </label>
          ))}
        </fieldset>
        <label htmlFor="api-key-expiry">Key expiry</label>
        <select
          id="api-key-expiry"
          value={expiryDays}
          onChange={(event) => setExpiryDays(event.target.value)}
        >
          <option value="never">No automatic expiry</option>
          <option value="30">In 30 days</option>
          <option value="90">In 90 days</option>
          <option value="365">In 365 days</option>
        </select>
        <fieldset className="api-key-retention">
          <legend>Content retention</legend>
          <label>
            <input
              type="radio"
              name="retention-mode"
              value="standard"
              checked={retentionMode === "standard"}
              onChange={() => setRetentionMode("standard")}
            />
            <span>
              <strong>Standard retention</strong>
              <small>
                Supports every model, private receipts, cache reuse, and
                idempotent replay.
              </small>
            </span>
          </label>
          <label>
            <input
              type="radio"
              name="retention-mode"
              value="zero_content"
              checked={retentionMode === "zero_content"}
              onChange={() => setRetentionMode("zero_content")}
            />
            <span>
              <strong>Zero customer-content retention</strong>
              <small>
                No stored text, result, receipt, cache, or replay record.
                Normalized character counts and costs are retained only for
                billing.
              </small>
            </span>
          </label>
          <p>This setting cannot be changed after the key is commissioned.</p>
        </fieldset>
        <button
          className="button primary"
          disabled={busy || !name.trim() || scopes.length === 0}
        >
          {busy ? "Commissioning…" : "Commission key"}
        </button>
      </form>

      <p className="muted">
        To rotate a key, commission a replacement with the same retention and
        required permissions, verify it in your application, then revoke the old
        key. Both work during the transition.
      </p>
      {issued && (
        <div className="issued-key" role="status">
          <strong>Copy this key now. It will not be shown again.</strong>
          <span>
            {issuedRetention === "zero_content"
              ? "Zero customer-content retention"
              : "Standard retention"}
          </span>
          <code>{issued}</code>
          <button
            type="button"
            className="button secondary"
            onClick={copyIssuedKey}
          >
            {copyState === "copied"
              ? "Copied to clipboard"
              : copyState === "failed"
                ? "Copy failed"
                : "Copy key"}
          </button>
        </div>
      )}
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}

      <div className="api-key-list">
        {keys.length === 0 && !error ? (
          <p className="empty-state">No API keys commissioned yet.</p>
        ) : (
          keys.map((key) => (
            <article key={key.public_id}>
              <div>
                <div className="api-key-name">
                  <h3>{key.name}</h3>
                  <span
                    className={
                      key.retention_mode === "zero_content"
                        ? "key-retention zero"
                        : "key-retention"
                    }
                  >
                    {key.retention_mode === "zero_content"
                      ? "Zero content"
                      : "Standard retention"}
                  </span>
                </div>
                <code>{key.prefix}</code>
                <p>
                  Permissions: {(key.scopes || []).join(", ") || "Unavailable"}
                </p>
                <p>
                  {key.expires_at
                    ? `${new Date(key.expires_at).getTime() <= Date.now() ? "Expired" : "Expires"} ${new Date(key.expires_at).toLocaleString()}`
                    : "No automatic expiry"}
                </p>
                <p>
                  Created {new Date(key.created_at).toLocaleDateString()}
                  {key.last_used_at
                    ? ` · Last used ${new Date(key.last_used_at).toLocaleDateString()}`
                    : " · Never used"}
                </p>
              </div>
              {key.revoked_at ? (
                <span className="key-revoked">Revoked</span>
              ) : (
                <button
                  className="button secondary"
                  onClick={() => revoke(key.public_id, key.name)}
                >
                  Revoke
                </button>
              )}
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function ConnectedBrowsers() {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  async function load() {
    setLoading(true);
    try {
      const response = await fetch("/api/v1/extension/sessions");
      if (!response.ok)
        throw new Error("Connected browsers could not be loaded.");
      setSessions((await response.json()).items || []);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Connected browsers could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function revoke(sessionId: string, browser: string) {
    if (
      !confirm(
        `Revoke this ${browser} browser session? Its extension will be signed out.`,
      )
    )
      return;
    const response = await fetch(`/api/v1/extension/sessions/${sessionId}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      setError("The browser session could not be revoked.");
      return;
    }
    await load();
  }
  return (
    <section
      className="connected-browsers"
      aria-labelledby="connected-browsers-heading"
    >
      <div>
        <h2 id="connected-browsers-heading">Connected browsers</h2>
        <p>
          Each activation creates a separate, revocable browser session.
          Revoking one does not sign out the website or your other browsers.
        </p>
      </div>
      {loading ? (
        <p className="empty-state" role="status">
          Loading connected browsers…
        </p>
      ) : error ? (
        <p className="alert error" role="alert">
          {error}
        </p>
      ) : sessions.length === 0 ? (
        <p className="empty-state">No browsers connected yet.</p>
      ) : (
        <div className="browser-session-list">
          {sessions.map((session) => (
            <article key={session.session_id}>
              <div>
                <h3>{session.browser === "firefox" ? "Firefox" : "Chrome"}</h3>
                <p>
                  Connected {new Date(session.created_at).toLocaleDateString()}{" "}
                  · Last used{" "}
                  {new Date(session.last_used_at).toLocaleDateString()}
                </p>
                {session.revoked_at && (
                  <span className="key-revoked">Revoked</span>
                )}
              </div>
              {!session.revoked_at && (
                <button
                  className="button secondary"
                  onClick={() => revoke(session.session_id, session.browser)}
                >
                  Revoke
                </button>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function Account() {
  const [data, setData] = useState<any>(null),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    let timer = 0;
    let checkoutPolls = 0;
    const load = () =>
      fetch("/api/v1/me")
        .then((r) => r.json())
        .then((body) => {
          if (!active) return;
          if (!body.authenticated) {
            location.replace(
              `/login?return_to=${encodeURIComponent(location.pathname + location.search + location.hash)}`,
            );
            return;
          }
          setData(body);
          const checkoutPending =
            new URLSearchParams(location.search).get("checkout") === "success";
          if (
            checkoutPending &&
            !["active", "past_due_grace"].includes(
              body.extension?.entitlement?.status,
            ) &&
            checkoutPolls++ < 30
          )
            timer = window.setTimeout(load, 2_000);
          else if (checkoutPending) history.replaceState({}, "", "/account");
        })
        .catch(() => setError("Account information could not be loaded."));
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);
  async function logout() {
    await fetch("/api/v1/auth/logout", { method: "POST" });
    location.href = "/";
  }
  if (error)
    return (
      <Page title="Account unavailable">
        <p className="alert error">{error}</p>
      </Page>
    );
  if (!data)
    return (
      <div className="page" aria-label="Loading account" aria-busy="true">
        <header className="page-title">
          <h1 className="account-title-placeholder" aria-hidden="true">
            &nbsp;
          </h1>
        </header>
        <section
          className="account-grid account-grid-loading"
          aria-hidden="true"
        >
          <article className="skeleton" />
          <article className="skeleton" />
          <article className="skeleton" />
        </section>
      </div>
    );
  const user = data.user;
  return (
    <Page title={`Hello, ${user?.display_name || "reader"}.`}>
      <section className="account-grid">
        <article>
          <h2>{user.display_name || user.email}</h2>
          <p>{user.email}</p>
          <button className="button secondary" onClick={logout}>
            Sign out
          </button>
        </article>
        <article>
          <h2>Free paste box</h2>
          <p>
            Individual paste-box scans remain free and separate from extension
            access.
          </p>
        </article>
        <article>
          <h2>Saved scans</h2>
          <p>
            Your scans are stored privately. Account history will appear here
            when the listing interface is available.
          </p>
        </article>
      </section>
      <section className="account-security" aria-labelledby="security-heading">
        <div>
          <h2 id="security-heading">Sign-in methods</h2>
          <p>
            {data.auth_methods
              ?.map((method: string) =>
                method === "google" ? "Google" : "Email and password",
              )
              .join(" · ")}
          </p>
        </div>
        {data.auth_methods?.includes("password") && <PasswordChange />}
      </section>
      <ExtensionBilling extension={data.extension} />
      <ConnectedBrowsers />
      <ApiCredits />
      <ApiKeyManager />
    </Page>
  );
}

function PasswordChange() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    const response = await fetch("/api/v1/auth/password/change", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    const body = await response.json().catch(() => null);
    setNotice(
      response.ok
        ? "Password changed. Other website sessions were signed out."
        : body?.error?.message || "Password change failed.",
    );
    setBusy(false);
    if (response.ok) {
      setCurrentPassword("");
      setNewPassword("");
    }
  }
  return (
    <form className="auth-form compact" onSubmit={submit}>
      <label htmlFor="current-password">Current password</label>
      <input
        id="current-password"
        type="password"
        autoComplete="current-password"
        required
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
      />
      <label htmlFor="account-new-password">New password</label>
      <input
        id="account-new-password"
        type="password"
        autoComplete="new-password"
        minLength={15}
        maxLength={128}
        required
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
      />
      {notice && (
        <p className="alert" role="status">
          {notice}
        </p>
      )}
      <button className="button secondary" disabled={busy}>
        {busy ? "Changing…" : "Change password"}
      </button>
    </form>
  );
}

function ExtensionBilling({ extension }: { extension: any }) {
  const [busy, setBusy] = useState<"monthly" | "annual" | "portal" | "">("");
  const [error, setError] = useState("");
  if (!extension) return null;
  const entitlement = extension.entitlement;
  const quota = extension.quota;
  const remainingPercent = allowanceRemainingPercent(
    quota.remaining_tokens,
    quota.limit_tokens,
  );
  const remainingLabel = formatAllowancePercent(remainingPercent);
  const usedPercent = 100 - remainingPercent;
  const labels: Record<string, string> = {
    trial_eligible: "14-day trial ready",
    trialing: "Free trial",
    active: `${entitlement.plan === "annual" ? "Annual" : "Monthly"} personal plan`,
    past_due_grace: "Payment retry period",
    trial_expired: "Trial ended",
    canceled: "Plan ended",
    disabled: "Billing review required",
  };
  const relevantDate =
    entitlement.status === "trialing"
      ? entitlement.trial_expires_at
      : entitlement.status === "past_due_grace"
        ? entitlement.grace_expires_at
        : entitlement.paid_through_at;
  async function open(
    path: "checkout" | "portal",
    plan?: "monthly" | "annual",
  ) {
    setBusy(plan || "portal");
    setError("");
    try {
      const response = await fetch(`/api/v1/billing/${path}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: path === "checkout" ? JSON.stringify({ plan }) : undefined,
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(body?.error?.message || "Billing could not be opened.");
      location.href = body.url;
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Billing could not be opened.",
      );
      setBusy("");
    }
  }
  return (
    <section
      className="extension-billing"
      aria-labelledby="extension-plan-heading"
    >
      <div className="billing-summary">
        <div>
          <span className="metadata-line">Browser extension</span>
          <h2 id="extension-plan-heading">
            {labels[entitlement.status] || "Personal access"}
          </h2>
          <p>
            {entitlement.status === "trial_eligible"
              ? "Your no-card trial begins with the first successful extension scan."
              : relevantDate
                ? `${entitlement.cancel_at_period_end ? "Access through" : entitlement.status === "active" ? "Renews" : "Access through"} ${new Date(relevantDate).toLocaleDateString()}.`
                : "Authentication, prior results, and local statistics remain available."}
          </p>
        </div>
        <div className="billing-actions">
          {!["active", "past_due_grace", "disabled"].includes(
            entitlement.status,
          ) && (
            <>
              <button
                className="button primary"
                disabled={!!busy}
                onClick={() => open("checkout", "monthly")}
              >
                {busy === "monthly" ? "Opening…" : "$19 monthly"}
              </button>
              <button
                className="button secondary"
                disabled={!!busy}
                onClick={() => open("checkout", "annual")}
              >
                {busy === "annual" ? "Opening…" : "$190 yearly · save $38"}
              </button>
            </>
          )}
          {extension.has_billing_customer && (
            <button
              className="button secondary"
              disabled={!!busy}
              onClick={() => open("portal")}
            >
              {busy === "portal" ? "Opening…" : "Billing portal"}
            </button>
          )}
        </div>
      </div>
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {entitlement.access_allowed === true ? (
        <div
          className={`account-quota ${usedPercent >= 80 ? "warning" : ""} ${quota.remaining_tokens === 0 ? "paused" : ""}`}
        >
          <div>
            <strong>{remainingLabel} of daily allowance remaining</strong>
            <span>rolling 24 hours</span>
          </div>
          <div
            className="account-quota-bar"
            role="progressbar"
            aria-label="Daily analysis allowance remaining"
            aria-valuetext={`${remainingLabel} remaining in the rolling 24-hour window`}
            aria-valuenow={Number(remainingPercent.toFixed(2))}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <i style={{ width: `${remainingPercent}%` }} />
          </div>
          <p>
            Your allowance replenishes continuously
            {quota.next_release_at
              ? `; more becomes available ${new Date(quota.next_release_at).toLocaleString()}`
              : " as earlier scans leave the rolling window"}
            .
          </p>
        </div>
      ) : (
        <div className="account-quota">
          <strong>Extension scanning unavailable</strong>
          <p>
            {entitlement.status === "disabled"
              ? "Resolve your billing status to resume extension scans."
              : "Choose a plan to resume extension scans."}
          </p>
        </div>
      )}
      <p className="fine">
        Personal use only. No scraping, resale, automated API use, or account
        sharing. Monthly/annual changes are scheduled for renewal without
        proration.
      </p>
    </section>
  );
}

function allowanceRemainingPercent(remaining: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.max(0, Math.min(100, (remaining / limit) * 100));
}

function formatAllowancePercent(percent: number): string {
  if (percent <= 0) return "0%";
  if (percent >= 100) return "100%";
  if (percent < 1) return "<1%";
  if (percent > 99) return ">99%";
  return `${Math.round(percent)}%`;
}

function Bounty() {
  const [status, setStatus] = useState("draft");
  const [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    const form = new FormData(e.currentTarget);
    try {
      const response = await fetch("/api/v1/bounty-submissions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          archive_url: form.get("archive"),
          text: form.get("text"),
          context: form.get("context"),
        }),
      });
      const body = await response.json();
      if (response.status === 401) {
        setStatus("signed_out");
        setError(body.error.message);
        return;
      }
      if (!response.ok)
        throw new Error(
          body?.error?.message || "Submission failed validation.",
        );
      setStatus(body.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed.");
      setStatus("error");
    }
  }
  return (
    <Page title="Report a false positive">
      <p className="lede">
        Submit human-written text captured by Archive.org before 2022 that
        Stylome classifies as AI generated. Accepted examples improve training;
        they never enter frozen evaluation holdouts.
      </p>
      <section className="split">
        <article>
          <h2>What qualifies</h2>
          <ol className="rules">
            <li>An Archive.org snapshot captured before January 1, 2022.</li>
            <li>At least 200 characters actually composed by a human.</li>
            <li>
              No machine translation, Markov text, templates, or generated
              feeds.
            </li>
            <li>
              Primitive LLM output, including GPT-2-era generated text, does not
              count as a false positive even when its snapshot predates 2022.
            </li>
            <li>
              One submission per normalized text hash; first verified submitter
              receives credit.
            </li>
          </ol>
          <div className="state-key">
            <span>Draft</span>
            <span>Submitted</span>
            <span>Queued</span>
            <span>Needs review</span>
            <span>Accepted</span>
            <span>Rejected</span>
            <span>Duplicate</span>
          </div>
          <p className="fine">
            The review workflow is defined. Payouts and adjudication will open
            with the public program launch.
          </p>
        </article>
        <form className="bounty-form" onSubmit={submit}>
          <h2>Submission preview</h2>
          <label>
            Archive snapshot URL
            <input
              name="archive"
              type="url"
              required
              pattern="https://web.archive.org/.*"
              placeholder="https://web.archive.org/web/2021…"
            />
          </label>
          <label>
            Claimed human text
            <textarea
              name="text"
              required
              minLength={200}
              maxLength={10000}
              placeholder="Paste at least 200 characters…"
            />
          </label>
          <label>
            Optional context
            <textarea
              name="context"
              maxLength={1000}
              placeholder="Why this appears human-authored…"
            />
          </label>
          <button className="button primary" disabled={status === "submitting"}>
            {status === "submitting" ? "Validating…" : "Submit candidate"}
          </button>
          {error && (
            <p className="alert error" role="alert">
              {error}{" "}
              {status === "signed_out" && (
                <Link to="/login?return_to=%2Fbounty">Sign in.</Link>
              )}
            </p>
          )}
          {status === "demonstration_only" && (
            <p className="alert notice">
              Validated as a demonstration. No payout or adjudication was
              created.
            </p>
          )}
        </form>
      </section>
      <section className="content-section">
        <h2>Your submissions</h2>
        <div className="empty-state">
          Sign in to view your submission history.
        </div>
      </section>
      <section className="content-section">
        <h2>Leaderboard</h2>
        <div className="empty-state">
          Verified submissions and contributor credits will appear here when
          public review begins.
        </div>
      </section>
    </Page>
  );
}

function Receipt({ id }: { id: string }) {
  const raw = localStorage.getItem(`receipt:${id}`);
  let data: any = null;
  try {
    data = raw ? JSON.parse(raw) : null;
    if (
      data?.model_version === "stylome-1.2-large" &&
      data.response_version !== PUBLIC_RESPONSE_VERSION
    ) {
      localStorage.removeItem(`receipt:${id}`);
      data = null;
    }
  } catch {
    data = null;
  }
  if (!data)
    return (
      <Page title="Receipt not found">
        <div className="empty-state">
          <p>
            We couldn't find a receipt at this link. Check the URL or return to
            the scanner.
          </p>
          <Link to="/" className="button primary">
            Scan text
          </Link>
        </div>
      </Page>
    );
  return (
    <Page title="Classifier receipt" metadata={`Receipt ${id}`}>
      <Result result={data} />
      <section className="receipt-copy">
        <h2>Complete document</h2>
        <p>{data.document_text}</p>
        <p className="fine">
          This immutable receipt records the classifier result for one document.
          It is not a judgment about an author, publisher, or site.
        </p>
        <button
          className="button secondary"
          onClick={() => navigator.clipboard?.writeText(location.href)}
        >
          Copy receipt link
        </button>
      </section>
    </Page>
  );
}

const policy: Record<string, { title: string; body: React.ReactNode }> = {
  "/privacy": {
    title: "Privacy",
    body: (
      <>
        <p className="fine">Effective September 23, 2026.</p>
        <p>
          Stylome operates this service from Pennsylvania, United States. This
          policy describes the information Stylome handles through the website,
          account system, paid browser extension, public paste box, and
          developer API.
        </p>
        <h2>Information Stylome handles</h2>
        <p>
          Account information includes the name and email address returned by
          Google sign-in, browser-session records, plan and entitlement state,
          and support communications. Standard-retention scan records include
          the submitted text, classifier result, timestamps, model and
          tokenizer-contract versions, and allowance-accounting records.
          Operational and security records can include IP address, browser or
          device information, request timing, error details, and
          abuse-prevention events. The developer API exception for
          zero-retention keys is described below.
        </p>
        <h2>Scans, history, and sharing</h2>
        <p>
          When a user selects a DOCX or EPUB file in the public scanner, the
          browser extracts its text on the user&apos;s device. EPUB books are
          read in their declared reading order and shown as selectable sections.
          Stylome does not receive the source file. Only the text shown in the
          scan form is submitted when the user chooses to scan it. Images,
          comments, scripts, navigation, and document styling are not submitted
          by the file importer.
        </p>
        <p>
          Except for developer requests made with a zero-retention key, Stylome
          stores the complete text submitted for a scan together with its result
          in account history. Results are not public unless the user chooses to
          publish a receipt. Publishing a receipt makes its submitted text and
          result available to anyone with the public link. Removing or unsharing
          a receipt ends public access through Stylome, but Stylome cannot
          recall copies someone else already made.
        </p>
        <p>
          When a person requests a public receipt page, Stylome records an
          aggregated receipt view. This uses a random first-party, HTTP-only
          visitor cookie, a one-way daily network and browser hash, and the
          referring hostname when available. Campaign ownership comes from the
          account that created the receipt; public links contain no employee or
          campaign tracking tag. Stylome does not retain the raw IP address or
          full referring URL in receipt-view analytics. Known automated link
          previews and crawlers are excluded.
        </p>
        <h2>Zero customer-content retention API keys</h2>
        <p>
          Where this option is available, a developer can choose zero
          customer-content retention when an API key is commissioned. The
          setting is fixed for that key. Eligible requests are normalized, sent
          over HTTPS to Stylome&apos;s private inference service, analyzed in
          transient memory, and returned to the caller. Stylome does not read or
          write the shared result cache for these requests.
        </p>
        <p>
          Stylome does not store the submitted text, an excerpt, a content hash,
          the classifier result, a receipt, or an idempotency record for a
          zero-retention request. The response can contain a content hash and
          classifier result for the caller&apos;s use, but Stylome does not
          retain them after the request. The response includes{" "}
          <code>X-Stylome-Data-Retention: none</code> and{" "}
          <code>Cache-Control: no-store</code>. Because replay requires stored
          request-derived data, these keys reject <code>Idempotency-Key</code>.
          Normalized character counts and exact USD costs are retained as
          billing metadata only, so prepaid charges can be reconciled.
        </p>
        <p>
          Stylome retains limited account, security, and operating metadata: the
          key&apos;s retention mode, request ID, timestamp, selected model and
          scoring contract, HTTP status, latency, normalized characters and USD
          cost, key last-used time, and rate-limit state. This metadata does not
          contain the submitted text or classifier output. Zero-retention keys
          support Stylome 1.2 Large and Stylome 1.0 Large.
        </p>
        <h2>Browser extension scanning</h2>
        <p>
          The extension displays badges on content it knows how to scan. With
          automatic scanning off, no discovered page text is sent until the user
          clicks a badge or requests a selected-text scan. If the user turns
          automatic scanning on, each scannable post or article they view is
          transmitted to Stylome over HTTPS, normalized, tokenized, analyzed,
          and stored with the result in account history. Automatic scanning can
          be turned off from the extension popup at any time.
        </p>
        <p>
          On X, the extension observes matching network responses from the
          page&apos;s fetch and XMLHttpRequest requests to read post data that X
          already loads. It keeps a limited, temporary copy of post IDs,
          previews, and complete text in page memory. This can happen while
          automatic scanning is off, without sending that text to Stylome. A
          requested scan, or an automatic scan that the user has enabled, can
          include the complete post even when part of it is hidden behind
          &ldquo;Show more.&rdquo; The extension does not expand posts or make
          extra requests to X to obtain this text. If complete text is
          unavailable, it waits for the user to expand the post before scanning.
          The reader does not copy cookies, authentication tokens, or request
          headers.
        </p>
        <p>
          The extension reads the current page URL to select supported site
          integrations. It does not read Chrome&apos;s browsing-history
          database. It keeps its display cache and up to 5,000 scan-activity
          records in trusted local browser storage. Each activity record
          contains the domain, scan time, result class, confidence, and hashed
          account and item identifiers. These records provide the popup&apos;s
          per-domain statistics; they do not store full page URLs, page titles,
          raw post identifiers, or submitted text. Stylome&apos;s servers do not
          receive these local activity records or domain statistics, and page
          domains and URLs are not collected for billing analytics. The
          extension does not keep a general log of clicks, mouse positions,
          scrolling, or keystrokes.
        </p>
        <h2>How information is used</h2>
        <p>
          Stylome uses this information to authenticate accounts, provide and
          improve the requested scanning features, maintain history and public
          shares, enforce plans and rolling allowances, provide support, secure
          the service, prevent abuse, comply with law, and maintain reliable
          operations. Submitted text is not sold, used for advertising, or used
          to train models without a separate explicit opt-in.
        </p>
        <p>
          People do not review submitted text except when the user specifically
          authorizes access for support, when access is necessary to investigate
          security or abuse, or when access is legally required. Analytics and
          operational events do not contain submitted documents, document
          excerpts, or receipt access tokens.
        </p>
        <h2>Service providers</h2>
        <p>
          Stylome uses Google for optional account sign-in, Resend for account
          verification and security email, Google Cloud for hosting, Cloudflare
          for network delivery and Turnstile abuse prevention, Hugging Face for
          private model artifact storage, RunPod infrastructure for classifier
          inference, and Stripe and Link for Checkout, billing, tax, receipts,
          subscription support, and payment processing. These providers receive
          the information necessary to perform their respective functions.
          Stylome may also disclose information when legally required or
          necessary to protect users, the service, or others.
        </p>
        <h2>Billing information</h2>
        <p>
          Link is the merchant of record for Managed Payments purchases. Stripe
          and Link receive the identity, address, payment, tax, and transaction
          information needed to sell and manage the subscription. Payment-card
          details are not sent to Stylome&apos;s servers. Stripe can cancel a
          subscription and remove associated Stripe records when it honors a
          Managed Payments deletion request.
        </p>
        <p>
          Stylome receives billing events from Stripe and stores customer and
          subscription identifiers, the selected plan, subscription and payment
          status, paid-through dates, and cancellation information. Payment
          success, failure, refund, and dispute events can affect subscription
          access. The extension receives and stores the plan, access status
          (including any past-due grace period), paid-through date, cancellation
          state, and whether a billing customer exists so it can display and
          enforce access. Payment details are entered in hosted Stripe/Link
          checkout, not in the extension. Neither the extension nor
          Stylome&apos;s servers receives full card numbers or card security
          codes through this checkout flow.
        </p>
        <h2>Retention and deletion</h2>
        <p>
          Account information and standard-retention scans, results, and shares
          are retained while needed to provide the account and until the user
          deletes them or asks Stylome to delete the account. Zero-retention
          requests do not add customer content to these stored records or to
          ordinary database backups. Until self-service deletion controls are
          available, requests can be sent to support@stylome.io. Deleting an
          account will cancel its subscription, revoke browser sessions, remove
          its active scan and receipt records, and remove public access to
          shared receipts, subject to records Stylome or its providers must
          retain for legal, tax, fraud, accounting, dispute, or security
          purposes.
        </p>
        <p>
          Disaster-recovery backups can retain residual copies until they are
          overwritten through ordinary backup rotation; they are isolated from
          normal product use. Stylome may retain a one-way account identifier
          solely to enforce the one-trial-per-account rule without retaining the
          original sign-in identifier in that record.
        </p>
        <p>
          Pseudonymous receipt-view rows are retained for up to 13 months for
          campaign measurement, fraud review, and compensation disputes.
          Aggregate counts that no longer identify a browser may be retained
          longer.
        </p>
        <h2>Children</h2>
        <p>
          Stylome subscriptions are intended only for people who are at least 18
          years old and have reached the age of legal majority where they live.
        </p>
        <h2>Browser-store limited use</h2>
        <p>
          Stylome&apos;s use and transfer of information received from Google
          APIs adheres to the Chrome Web Store User Data Policy, including the
          Limited Use requirements. Browser permissions and website content are
          used only for the disclosed scanning feature, related product
          operations, security, and support.
        </p>
        <h2>Website analytics and advertising</h2>
        <p>
          We use Google Analytics cookies to measure visits and engagement on
          public pages. We do not send scan text, results, account details,
          receipt identifiers, or URL query parameters and fragments to Google,
          except ad click identifiers and campaign tags. Account, sign-in,
          password, and receipt pages are excluded. See{" "}
          <a href="https://policies.google.com/privacy">
            Google&apos;s privacy policy
          </a>{" "}
          for its data practices.
        </p>
        <p>
          We advertise Stylome with Google Ads. When you arrive from one of our
          ads, Google advertising cookies record the ad click so we can measure
          whether it led to an extension install or a subscription. Google may
          also use visits to our public pages to show you Stylome ads on other
          sites. You can control ad personalization in{" "}
          <a href="https://myadcenter.google.com/">
            Google&apos;s My Ad Center
          </a>
          . We do not set advertising cookies for visitors in the European
          Economic Area, the United Kingdom, or Switzerland.
        </p>
        <h2>Human verification</h2>
        <p>
          The scan form uses Cloudflare Turnstile in invisible mode to
          distinguish legitimate visitors from automated abuse. No verification
          control is normally shown. Cloudflare processes browser and network
          signals for this purpose under its{" "}
          <a
            href="https://www.cloudflare.com/en-gb/turnstile-privacy-policy/"
            target="_blank"
            rel="noreferrer"
          >
            Turnstile Privacy Addendum
          </a>
          .
        </p>
        <h2>Policy changes and contact</h2>
        <p>
          Stylome may update this policy by posting a revised version and
          effective date. Additional notice or consent will be provided only
          where applicable law or browser-store rules require it. Privacy and
          deletion questions can be sent to support@stylome.io.
        </p>
      </>
    ),
  },
  "/terms": {
    title: "Terms",
    body: (
      <>
        <p className="fine">Effective July 21, 2026.</p>
        <p>
          These terms govern use of Stylome&apos;s website, account system,
          classifier, and browser extension. By creating an account, purchasing
          a subscription, or using the service, you agree to them.
        </p>
        <h2>Eligibility</h2>
        <p>
          You must be at least 18 years old and have reached the age of legal
          majority where you live. You must provide accurate account and billing
          information and use Stylome only where its use is lawful and
          available. Stylome is operated from Pennsylvania, United States.
        </p>
        <h2>Personal extension plan</h2>
        <p>
          Personal extension access is for one Stylome account. Scraping,
          resale, automated API use, and account sharing are not included. The
          public paste box remains free, and API access is a separate product.
          Monthly or annual changes requested through the billing portal take
          effect at renewal without proration.
        </p>
        <h2>Trials, billing, and cancellation</h2>
        <p>
          A Stylome account receives one 14-day, no-card extension trial,
          beginning with its first successful extension scan. A paid purchase is
          charged immediately and ends any remaining app-managed trial.
          Subscriptions renew automatically until canceled. Cancellation keeps
          access through the paid-through date and stops the next renewal.
          Failed renewal payments can receive a three-day access grace period.
        </p>
        <p>
          Link is the merchant of record for Managed Payments purchases. The
          website displays base prices in US dollars; Checkout can display local
          currency and adds or includes applicable tax based on the
          purchaser&apos;s location. Stripe and Link send transaction and
          subscription messages and provide transaction-level support.
        </p>
        <h2>Refunds</h2>
        <p>
          Stylome will approve a refund for an initial purchase requested within
          seven days, an accidental renewal requested within three days, a
          duplicate or erroneous charge, or a verified failure to provide paid
          access. Other refunds are discretionary except where law requires
          otherwise. Canceling outside these circumstances does not create a
          prorated refund. Refund requests may be sent to support@stylome.io or
          Link support. Stripe may also issue refunds under its Managed Payments
          policies.
        </p>
        <h2>Submitted material</h2>
        <p>
          You keep your rights in submitted text and grant Stylome the limited
          permission needed to transmit, normalize, tokenize, analyze, store,
          display, and, when you explicitly share a receipt, publish that text
          and its result. You may submit only material you are legally permitted
          to access and analyze. Do not submit confidential, private, illegally
          obtained, or highly sensitive personal information without proper
          authorization.
        </p>
        <h2>Acceptable use</h2>
        <p>
          You may not scrape or automate the personal extension, resell access,
          emulate an API through the extension, share an account, bypass quotas
          or safeguards, interfere with the service, commit billing fraud, use
          Stylome for unlawful surveillance or harassment, or use the service in
          violation of another person&apos;s rights. You are responsible for how
          you interpret and use scan results.
        </p>
        <h2>Classifier results</h2>
        <p>
          Stylome reports classifier signals about the submitted passage. A
          result is not a representation by Stylome that any particular person
          authored or did not author the passage. Models, thresholds, supported
          sites, features, and allowances can change as the product develops.
        </p>
        <h2>Suspension and termination</h2>
        <p>
          Stylome may suspend scanning or terminate access for nonpayment, quota
          exhaustion, abuse, security threats, unlawful activity, breach of
          these terms, or conduct that threatens the service or others. Ordinary
          trial expiry, cancellation, and quota exhaustion do not by themselves
          delete account history. Serious abuse can result in full account
          termination.
        </p>
        <h2>No warranties</h2>
        <p>
          To the maximum extent permitted by law, Stylome is provided “as is”
          and “as available,” without warranties, guarantees, service-level
          commitments, or promises that every site, passage, browser, model, or
          feature will remain available.
        </p>
        <h2>Exclusion of liability</h2>
        <p>
          To the maximum extent permitted by law, Stylome and its operator will
          not be liable for any direct, indirect, incidental, consequential,
          special, exemplary, punitive, reputational, academic, employment,
          business, data, or other loss or damage arising from or related to the
          service or any result. Where applicable law does not permit complete
          exclusion, total liability will not exceed the amount paid for Stylome
          during the 12 months before the event giving rise to the claim. Rights
          and liabilities that cannot legally be excluded remain unaffected.
        </p>
        <h2>Governing law</h2>
        <p>
          These terms are governed by Pennsylvania law, without regard to
          conflict-of-law rules. Disputes may be brought in the appropriate
          state or federal courts serving York County, Pennsylvania. Mandatory
          consumer protections in a user&apos;s home jurisdiction remain
          unaffected where they apply.
        </p>
        <h2>Changes and contact</h2>
        <p>
          Stylome may change these terms, features, allowances, or policies by
          posting a revised version and effective date. Changes apply upon
          posting unless Stylome states otherwise; additional notice or consent
          will be provided only where applicable law or browser-store rules
          require it. Price changes apply no earlier than a future renewal.
          Questions can be sent to support@stylome.io.
        </p>
      </>
    ),
  },
  "/methodology": {
    title: "Methodology",
    body: (
      <>
        <p className="fine">Updated July 27, 2026.</p>
        <p>
          Stylome treats an AI-text detector as a measurement system, not an
          authorship oracle. A public result must be tied to a named model,
          documented test population, fixed decision threshold, and known
          limitations. The classifier output is model evidence and must not be
          used as proof of who wrote a passage.
        </p>
        <h2>Freeze the evidence before tuning</h2>
        <p>
          Measurement holdouts are frozen before they are used to judge a model.
          Their normalized content hashes are audited against training and
          calibration inputs before a result is reported. If overlap is found,
          the affected measurement is not treated as independent evidence.
        </p>
        <h2>Compare at the same false-positive rate</h2>
        <p>
          Raw deployed accuracy numbers are not comparable when detectors use
          different thresholds. Stylome compares models at matched
          false-positive rates and reports the threshold, sample counts, and
          uncertainty needed to interpret the comparison.
        </p>
        <h2>Do not hide register shortcuts inside an aggregate</h2>
        <p>
          Results are decomposed by source and writing register—including news,
          reviews, educational writing, creative prose, email, and ESL-focused
          sets where available. Tail errors matter: an attractive overall score
          does not excuse concentrated false positives on one kind of writer.
        </p>
        <h2>Interpret the three authorship classes</h2>
        <dl className="definitions">
          {resultMeanings.map(([label, meaning]) => (
            <React.Fragment key={label}>
              <dt
                className={
                  label === "Human written"
                    ? "result-label-human"
                    : label === "AI edited"
                      ? "result-label-mixed"
                      : "result-label-ai"
                }
              >
                {label}
              </dt>
              <dd>{meaning}</dd>
            </React.Fragment>
          ))}
        </dl>
        <p>
          Confidence is separate from class. Low, medium, or high confidence
          describes relative KNN support from neighbor agreement, proximity, and
          binary decision margin compared with calibration examples. It is not a
          probability of correctness or proof of authorship. AI edited does not
          mean an inconclusive result.
        </p>
        <h2>Current deployed contract</h2>
        <p>
          The paste box and browser extension use Stylome 1.2 Large at the 0.01%
          calibration FPR target. Inputs contain 200–10,000 characters after
          whitespace-only normalization, with Unicode distinctions preserved and
          no silent truncation. The developer API accepts stylome-1.2-large or
          stylome-1.0-large. Only 1.2 accepts a 0.01% (default) or 0.1%
          operating point and provides relative KNN confidence. Large 1.0
          retains its separate score-based response. Each model has a frozen
          scoring and input contract. A calibration FPR target is not a
          guaranteed error rate on new text. Historical benchmark reports retain
          the labels and thresholds of the model they evaluated.
        </p>
        <p>
          The methodology and published measurements serve different jobs: this
          page documents the rules for earning a claim; the transparency report
          shows the attributable results that currently meet those rules.
        </p>
        <Link to="/transparency" className="button secondary">
          View published measurements
        </Link>
      </>
    ),
  },
};
function Page({
  title,
  metadata,
  children,
}: {
  title: string;
  metadata?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="page">
      <header className="page-title">
        <h1>{title}</h1>
        {metadata && <p className="metadata-line">{metadata}</p>}
      </header>
      {children}
    </div>
  );
}
function Policy({ route }: { route: string }) {
  const p = policy[route];
  return (
    <Page title={p.title}>
      <section className="prose">{p.body}</section>
    </Page>
  );
}
function NotFound() {
  return (
    <Page title="Page not found" metadata="HTTP 404">
      <div className="empty-state">
        <p>The requested page does not exist.</p>
        <Link to="/" className="button primary">
          Return to scanner
        </Link>
      </div>
    </Page>
  );
}

function ServicePage({ maintenance = false }: { maintenance?: boolean }) {
  return (
    <Page
      title={maintenance ? "Scheduled maintenance" : "Something went wrong"}
      metadata={maintenance ? undefined : "HTTP 500"}
    >
      <div className="empty-state">
        <p>
          {maintenance
            ? "The website is temporarily read-only while service work completes."
            : "The page could not be rendered. No scan or receipt was changed."}
        </p>
        <button className="button primary" onClick={() => location.reload()}>
          Retry
        </button>
      </div>
    </Page>
  );
}

function applyRouteMetadata(route: string) {
  const metadata = seoForRoute(route);
  const socialImage = metadata.socialImage || SOCIAL_IMAGE;
  const socialImageAlt =
    metadata.socialImageAlt ||
    "Stylome 1.0 EditLens results from July 2026: 5,896 of 5,931 generated passages detected; 6,068 of 6,068 human passages cleared";
  document.title = metadata.title;
  const values: Array<[string, string, string]> = [
    ["meta[name='description']", "content", metadata.description],
    [
      "meta[name='robots']",
      "content",
      metadata.indexable
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    ],
    ["link[rel='canonical']", "href", canonicalForRoute(route)],
    ["meta[property='og:title']", "content", metadata.title],
    ["meta[property='og:description']", "content", metadata.description],
    ["meta[property='og:url']", "content", canonicalForRoute(route)],
    ["meta[property='og:image']", "content", socialImage],
    ["meta[property='og:image:alt']", "content", socialImageAlt],
    ["meta[name='twitter:title']", "content", metadata.title],
    ["meta[name='twitter:description']", "content", metadata.description],
    ["meta[name='twitter:image']", "content", socialImage],
  ];
  for (const [selector, attribute, value] of values) {
    document.querySelector(selector)?.setAttribute(attribute, value);
  }
}

export function App({ initialRoute }: { initialRoute?: string }) {
  const [route, setRoute] = useState(initialRoute || path());
  useEffect(() => {
    const f = () => setRoute(path());
    addEventListener("popstate", f);
    return () => removeEventListener("popstate", f);
  }, []);
  useEffect(() => {
    applyRouteMetadata(route);
    trackPage(route, location.search);
    if (route === "/welcome") trackInstallOnce(location.search);
  }, [route]);
  const content = useMemo(() => {
    if (route === "/") return <Home />;
    if (route === "/transparency") return <Transparency />;
    if (route === "/pricing") return <Pricing />;
    if (route === "/supported-sites") return <SupportedSitesPage />;
    if (route === "/developers") return <Developers />;
    if (route === "/welcome") return <Welcome />;
    if (route === "/extension") return <ExtensionPage />;
    if (route === "/login") return <Login />;
    if (route === "/register") return <EmailRequestPage />;
    if (route === "/verify-email") return <PasswordCompletePage />;
    if (route === "/forgot-password") return <EmailRequestPage reset />;
    if (route === "/reset-password") return <PasswordCompletePage reset />;
    if (route === "/account") return <Account />;
    if (route === "/bounty") return <Bounty />;
    if (route === "/500") return <ServicePage />;
    if (route === "/maintenance") return <ServicePage maintenance />;
    if (policy[route]) return <Policy route={route} />;
    if (route.startsWith("/r/")) return <Receipt id={route.slice(3)} />;
    return <NotFound />;
  }, [route]);
  return <Layout route={route}>{content}</Layout>;
}

if (typeof document !== "undefined") {
  const root = document.getElementById("root")!;
  const application = (
    <React.StrictMode>
      <App initialRoute={path()} />
    </React.StrictMode>
  );
  if (root.hasChildNodes()) hydrateRoot(root, application);
  else createRoot(root).render(application);
}
